// src/components/ListingsTab.jsx
// HR monitoring view of agent posting activity from the senTavus extension.
// - Agents ordered: in-office → remote → absent → off (zero-activity agents shown too)
// - One shared adaptive timeline: empty night hours shrink, busy periods expand
// - Each listing is a clickable pin (opens the offer URL), colored by platform
import { useState, useEffect, useMemo, useRef, memo, createContext, useContext, useCallback } from 'react'
import { supabase } from '../db/index.js'
import { useAttendance } from '../hooks/useAttendance.js'
import { useOverrides } from '../hooks/useOverrides.js'
import { scheduledLocation, toDateStr, monthBounds } from '../lib/attendance.js'
import { scheduledSlotsOnDate, timeToMinutes } from '../lib/schedule.js'
import { tbilisiDayWindow, tbilisiMinuteOfDay, tbilisiParts, tbilisiToday, fmtTbilisi } from '../lib/tbilisiTime.js'
import { t, tLog, tOr, getLanguage } from '../lib/i18n.js'
import { agentEventKeys as agentEmailKeys } from '../lib/performance.js'
import { Icon } from './Icon.jsx'
import { can } from '../lib/permissions.js'
import { COUNTER } from './desk/primitives.jsx'
import { getMeetingMarkers, getMeetingNudges, getSchedulerRoster, getPortalBalances, getAutoPostIds, getWorkerSwitches, setWorkerSwitchPart, setPaidOverride, getMyWorker, getExtSessions, getCooperationsOpened } from '../db/core.js'
import { DateInput } from './SegmentedInput.jsx'
import { MEETING_STATUS_KEYS } from './desk/MeetingsFeed.jsx'
import { COOP_FILL, COOP_EDGE } from '../lib/coopMark.js'

const testDb = supabase

// one shared empty array, so memoised rails don't see a "new" prop on every render
const EMPTY = []

// Exported: the Desk (IntakeTab) reuses these so platform noting/coloring stays
// identical across tabs (korter=orange, ss=blue, myhome=green, home=lime).
// home.ge added 2026-07-23 (owner, with brand screenshots): both myhome and home.ge
// brand GREEN, so they get distinct shades — myhome keeps deep emerald, home.ge
// gets the lighter lime matching its own lighter brand green.
// facebook = our OWN outgoing group posts (register_publication). facebook_group = a listing
// we SCRAPED from someone else's group post (owner ruling 2026-08-28: keep them separate). Sky
// for ours, indigo for scraped — same family, visibly distinct.
export const PLATFORM_LABEL = { korter: 'Korter', ss: 'SS.ge', myhome: 'MyHome', home: 'Home.ge', facebook: 'Facebook', facebook_group: 'FB ჯგუფი' }
// Compact labels for the platform tallies on narrow (mobile) screens.
const PLATFORM_SHORT = { korter: 'Kort', ss: 'SS', myhome: 'My', home: 'Hm', facebook: 'FB', facebook_group: 'FBჯ' }
// Single-letter labels for the end-of-track tallies (keeps them tiny → longer track).
const PLATFORM_LETTER = { korter: 'K', ss: 'S', myhome: 'M', home: 'H', facebook: 'F', facebook_group: 'Ⓕ' }
export const PLATFORM_COLOR = {
  korter:  'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-400',
  ss:      'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400',
  myhome:  'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400',
  home:    'bg-lime-100 text-lime-700 dark:bg-lime-900/30 dark:text-lime-400',
  facebook: 'bg-sky-100 text-sky-700 dark:bg-sky-900/30 dark:text-sky-400',
  facebook_group: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400',
}
// Core listing rows store source as a domain-ish string ("myhome.ge", "home.ss.ge",
// "korter.ge", "home.ge") — map it onto the platform keys above; null when unrecognized.
// ⚠ Ordering is load-bearing: "myhome.ge" CONTAINS the substring "home.ge", so the
// myhome check must return before the home check runs ("home.ss.ge" is safe either
// way — it carries "home.ss", not "home.ge").
export function platformKey(source) {
  const s = (source || '').toLowerCase()
  if (s.includes('myhome')) return 'myhome'
  if (s.includes('korter')) return 'korter'
  if (s.includes('ss.ge') || s === 'ss') return 'ss'
  if (s.includes('home.ge')) return 'home'
  // ⚠ facebook_group must be tested BEFORE facebook — it CONTAINS "facebook", so the
  // scraped-group key would otherwise collapse into our own-posts key.
  if (s.includes('facebook_group') || s.includes('fbgroup')) return 'facebook_group'
  if (s.includes('facebook') || s === 'fb') return 'facebook'
  return null
}
// Solid pin colors (timeline) — also reused by the Desk map view (IntakeTab) for its per-platform
// pins, so this stays the one place the brand colors are defined.
export const PIN_COLOR = { korter: '#f97316', ss: '#3b82f6', myhome: '#10b981', home: '#84cc16', facebook: '#1877f2', facebook_group: '#4f46e5' }

// ─── ONE VISUAL SYSTEM FOR THIS PAGE (owner 2026-11-03: "layout things were added from time to time and placement got
// mingled"). Every status colour, card and column on the Activity page comes from the few constants below. A new element
// takes one of them - never its own tone, padding or width - or the page drifts apart again.
//
// TONE = the four states anything here can be in. KPI tiles, balance gauges, row status, the rail's
// PC strip and the legend all read it. `fill` is for drawn shapes (strips, dots), the rest are classes.
const TONE = {
  ok:    { text: 'text-emerald-600 dark:text-emerald-400', chip: '!bg-emerald-100 !text-emerald-700 dark:!bg-emerald-900/30 dark:!text-emerald-400', fill: '#10b981' },
  warn:  { text: 'text-amber-600 dark:text-amber-400',     chip: '!bg-amber-100 !text-amber-800 dark:!bg-amber-900/30 dark:!text-amber-300',       fill: '#f59e0b' },
  bad:   { text: 'text-red-600 dark:text-red-400',         chip: '!bg-red-100 !text-red-700 dark:!bg-red-900/30 dark:!text-red-400',               fill: '#ef4444' },
  muted: { text: 'text-muted-foreground',                  chip: '!bg-muted !text-muted-foreground',                                            fill: 'rgba(148,163,184,0.45)' },
}
// CATEGORIES, not states - each drawn the same wherever it appears (rail, section header, legend)
const AUTO_FILL = '#a855f7'                       // automatic mode on (strip under a rail)
// a cooperation opened (diamond on the rail): COOP_FILL / COOP_EDGE from lib/coopMark.js (amber read as Korter's orange)
const HOME_TONE = 'text-blue-500'                 // working from home (section header)
const SHIFT_FILL = {                              // the scheduled shift band on a rail
  office: { tint: 'rgba(16,185,129,0.07)', edge: 'rgba(16,185,129,0.3)' },
  home:   { tint: 'rgba(59,130,246,0.06)', edge: 'rgba(59,130,246,0.28)' },
}
// meeting nudge tier (0427): the ring on the glyph and the dot after the name
const NUDGE_FILL = { critical: TONE.bad.fill, high: '#f97316', medium: TONE.warn.fill }
// the outer container of EVERY block on the page, and the inner tile of every number
const CARD = 'bg-card border border-border rounded-xl'
const TILE = 'rounded-lg bg-muted/40 px-3 py-2 min-w-0'
// "when was it read" - DD.MM HH:MM in Tbilisi time, the same in both languages (a browser locale printed 11/3/26, 12:00 PM
// inside the Georgian UI)
const readAt = iso => { if (!iso) return '—'; const p = tbilisiParts(iso); return `${p.date.slice(8, 10)}.${p.date.slice(5, 7)} ${p.time}` }

// Meetings on the activity rail (owner 2026-08-01). A posting is a keystroke; a
// meeting is the day's actual appointment, so it reads as a landmark on the track
// rather than a differently-coloured dot among the pins. The glyph IS the status —
// closed is a trophy, because "did the viewing turn into a deal" is the only
// question an admin scans this rail for. Statuses come from MEETING_STATUS_KEYS
// (desk/MeetingsFeed.jsx), the one registry of the five stored values; an unknown
// status falls back to the calendar rather than rendering nothing.
//
// Icon.jsx (inlined Lucide), NOT emoji: this is on-screen, where the house rule is
// SVG — and here it pays twice, because an emoji ignores `color`, so the status
// could only be read from the picture. These inherit currentColor, so the tone
// below carries the same signal the Desk's meeting badges use. ⚠️ Icon returns
// null for a name that isn't in PATHS — verify any name added here.
const MEETING_ICON = {
  scheduled: 'calendar', pending: 'hourglass', binder: 'handshake', closed: 'trophy', failed: 'x',
  // 'ban' = called off. Distinct from failed's ✗ on purpose: the rail is scanned to tell
  // a spent hour from a freed one, and one glyph for both makes that unanswerable.
  cancelled: 'ban',
  // 'moved' is a MARKER KIND, not a stored status (it never went near meeting_status_check):
  // the server mints it from the meeting.rescheduled event so the day an appointment LEFT
  // still says so. Without it a reschedule rewrites history — the rail would show the agent
  // as having been free all along, which is the one thing that hour certainly was not.
  moved: 'calendar',
  // 'revisited' (0690) is a MARKER KIND too: this visit was followed by another visit of the
  // same deal, so its outcome lives on the later one. The trophy goes where the deal closed.
  revisited: 'repeat',
}
const MEETING_TONE = {
  scheduled: 'text-blue-500',
  pending:   'text-yellow-500',
  binder:    'text-amber-500',
  closed:    TONE.ok.text,
  failed:    TONE.bad.text,
  cancelled: TONE.muted.text,
  moved:     TONE.muted.text,
  revisited: 'text-emerald-500/70',
}
// How loudly each state speaks (owner 2026-08-03). An appointment that HAPPENED or is still
// coming is a landmark; one that was cancelled or moved away is a fact about the past that
// must not compete with the pins next to it — "become very transparent". The pins are the
// day's work, and work continues after a meeting falls through, so a dead meeting must never
// out-shout live posting.
const MEETING_OPACITY = { failed: 0.3, cancelled: 0.25, moved: 0.22, revisited: 0.6 }
const MEETING_ICON_PX = 30

// Status groups, in display order (labelKey resolved at render time)
const GROUPS = [
  { key: 'office', labelKey: 'listings_group_office', icon: 'building',    color: TONE.ok.text },
  { key: 'remote', labelKey: 'listings_group_remote', icon: 'home',        color: HOME_TONE },
  { key: 'absent', labelKey: 'listings_group_absent', icon: 'x',           color: TONE.bad.text },
  { key: 'off',    labelKey: 'listings_group_off',    icon: 'moon',        color: TONE.muted.text },
  // logins with events that match NO agent in the whole roster (test accounts, a login nobody linked) - their own
  // section, never counted in the headline or in an office's denominator (audit 2026-10-21)
  { key: 'unmatched', labelKey: 'listings_group_unmatched', icon: 'help-circle', color: TONE.muted.text },
]
const GROUP_ORDER = { office: 0, remote: 1, absent: 2, off: 3, unmatched: 4 }

// ⚠️ THE one outcome of a posting session - the headline, the row counters, the platform tallies, the dots and the
// expanded history all read it (audit 2026-10-21). They used to disagree: the headline counted completed&&!published
// as failed, the rows counted failed&&!published, and the history called every unfinished session "cancelled", so a
// pending post read as cancelled and a definitive failure could be missing from the headline. Nothing in user_events
// records a cancellation, so there is no 'cancelled' outcome - an unfinished session is pending.
export function outcomeOf(ev) {
  if (ev.published) return 'published'
  if (ev.failed) return 'failed'
  if (ev.completed) return 'filled'   // form filled, no link captured
  return 'pending'
}
const OUTCOME_LABEL = { published: 'listings_status_published', failed: 'listings_status_failed', filled: 'listings_status_filled', pending: 'listings_status_pending' }
const OUTCOME_TONE = { published: TONE.ok.text, failed: TONE.bad.text, filled: TONE.warn.text, pending: TONE.muted.text }

// Advert id at the end of the offer URL - what share_plans' automatic runs are matched by
function advertIdOf(ev) {
  return String(ev.offer_url || '').match(/(\d{5,})(?!.*\d{5,})/)?.[1] || null
}
function isAutoPost(ev, autoIds) {
  const id = ev.published ? advertIdOf(ev) : null
  return !!(id && autoIds && autoIds.has(id))
}

// The day's real check-in, or null. 'HH:MM' strings on the attendance record (legacy single or per slot).
function checkInOf(agentId, date, attByAgent) {
  const rec = attByAgent?.[agentId]?.[date]
  const raw = rec?.check_in || (Array.isArray(rec?.slots) ? rec.slots.find(s => s.check_in)?.check_in : null)
  return raw ? String(raw).replace(/^(\d{1,2}:\d{2}).*$/, '$1') : null
}

// ─── helpers ──────────────────────────────────────────────────────────────────

// Minute-of-day (TBILISI time) for an ISO timestamp — the rail draws Georgian wall-clock
// positions, never the device's (owner ruling 2026-08-12).
const minuteOfDay = tbilisiMinuteOfDay
function fmtClock(iso) {
  return tbilisiParts(iso).time
}

// Was this agent employed on `date`? Active agents always (from hire date on);
// resigned agents (is_active===false) only on dates before their resign_date — so
// past days still show people who were working back then.
function employedOn(agent, date) {
  if (agent.hire_date && date < agent.hire_date) return false   // not hired yet
  if (agent.is_active === false) {
    return agent.resign_date ? date < agent.resign_date : false
  }
  return true
}

// Status group for an agent on a date — mirrors AttendanceTab DayView logic.
function statusOf(agent, date, attByAgent, scheduleHistory, overrideMap) {
  const rec = attByAgent?.[agent.id]?.[date]
  if (rec?.status === 'absent') return 'absent'
  const loc = scheduledLocation(agent, date, scheduleHistory, overrideMap)
  // A day-off (incl. a one-off schedule override) means the agent isn't scheduled.
  // A stale/placeholder "present" record with NO actual check-in must not drag an
  // overridden-off agent into "office" (2026-06 bug: agents overridden off still
  // showed under In-office). A real check-in still wins and keeps them office/remote.
  const hasRealCheckIn = !!(rec?.check_in || (Array.isArray(rec?.slots) && rec.slots.some(s => s.check_in)))
  if (loc === 'off' && !hasRealCheckIn) return 'off'
  const status = rec?.status || (loc !== 'off' ? 'assumed' : 'off')
  if (status === 'present' || (status === 'assumed' && loc !== 'off' && loc !== 'home')) return 'office'
  if (status === 'remote'  || (status === 'assumed' && loc === 'home')) return 'remote'
  return 'off'
}

// Agent → event join keys: imported from lib/performance.js (single source of
// truth — login_id is the real key, email a fallback). Aliased to the original
// name so the call sites below are unchanged.

// Build a shared piecewise-linear time scale from ALL events for the day.
// Empty 30-min bins collapse to a sliver; active bins expand with activity.
//
// ⚠️ `extraMinutes` (meetings) MUST be counted here too, not just drawn. An empty bin
// is 3px wide, so a 15:00 viewing in an hour where nobody posted would land inside a
// sliver — several agents' meetings stacking on the same 3 pixels, unreadable and
// unhoverable. Anything the rail DRAWS has to widen the bin it is drawn in.
function buildScale(allEvents, extraMinutes = [], minW = 0) {
  const BIN_MIN = 30
  const bins = 1440 / BIN_MIN // 48
  const counts = new Array(bins).fill(0)
  for (const ev of allEvents) {
    const b = Math.min(bins - 1, Math.max(0, Math.floor(minuteOfDay(ev.created_at) / BIN_MIN)))
    counts[b]++
  }
  for (const m of extraMinutes) {
    const b = Math.min(bins - 1, Math.max(0, Math.floor(m / BIN_MIN)))
    counts[b]++
  }
  const EMPTY_W = 3, BASE_W = 16, PER_EVT = 7, MAX_W = 64
  const raw = counts.map(c => (c === 0 ? EMPTY_W : Math.min(MAX_W, BASE_W + PER_EVT * c)))
  // TRACK FIRST (owner 2026-11-03): a day narrower than the rail is stretched to fill it, every bin by the same factor,
  // so the shape (busy hours wide, empty hours thin) is kept and no rail ends half way across the card
  const rawW = raw.reduce((a, b) => a + b, 0)
  const k = minW > rawW ? minW / rawW : 1
  const width = raw.map(w => w * k)
  const xStart = []
  let acc = 0
  for (let i = 0; i < bins; i++) { xStart[i] = acc; acc += width[i] }
  const totalW = acc || 1
  const xForMinute = (m) => {
    const mm = Math.min(1440, Math.max(0, m))
    const b = Math.min(bins - 1, Math.floor(mm / BIN_MIN))
    const frac = (mm - b * BIN_MIN) / BIN_MIN
    return xStart[b] + frac * width[b]
  }
  // the inverse, for a tap on the rail (which minute was touched) - see AgentTimeline
  const minuteForX = (x) => {
    const xx = Math.min(totalW, Math.max(0, x))
    let b = 0
    while (b < bins - 1 && xStart[b + 1] <= xx) b++
    return Math.min(1440, b * BIN_MIN + ((xx - xStart[b]) / (width[b] || 1)) * BIN_MIN)
  }
  return { bins, counts, width, xStart, totalW, xForMinute, minuteForX, BIN_MIN }
}

function groupByUser(events) {
  const map = {}
  for (const ev of events) {
    const key = (ev.user_email || ev.user_id || 'unknown').toLowerCase()
    if (!map[key]) map[key] = []
    map[key].push(ev)
  }
  return map
}

// ─── small UI bits ──────────────────────────────────────────────────────────

// TOUCH (owner 2026-11-03, Activity redesign): a control in a row is at least 44 px on a touch screen and stays compact
// under a mouse. `pointer: coarse` rather than a width breakpoint - a tablet in landscape is wide AND touched.
const TAP = '[@media(pointer:coarse)]:min-h-[44px] [@media(pointer:coarse)]:min-w-[44px]'
const isCoarse = () => typeof window !== 'undefined' && !!window.matchMedia?.('(pointer: coarse)').matches

// TAP-TO-REVEAL (same redesign): every fact that used to live only in a `title` tooltip - a pin's outcome and reason, a
// band's on/off, a balance's timestamp, the version - is also shown in a small popover on a tap, because a phone has no
// hover. ONE popover for the page; `show(text, anchor, link)` where anchor is an element or a {x, y} point.
const TipContext = createContext(() => {})
const useTip = () => useContext(TipContext)

// A small panel floating beside an anchor rect - FIXED, so a section's overflow-hidden cannot clip it. Closes on a tap
// outside, Escape, or a scroll outside it (the anchor has moved away). The tip popover and the row's ⋯ menu share it.
function FloatingPanel({ anchor, onClose, label, className = '', children }) {
  const ref = useRef(null)
  const [pos, setPos] = useState(null)
  useEffect(() => {
    const el = ref.current
    const place = () => {
      const w = el ? el.offsetWidth : 260, h = el ? el.offsetHeight : 60
      const vw = window.innerWidth, vh = window.innerHeight
      let top = anchor.bottom + 6
      if (top + h > vh - 8) top = Math.max(8, anchor.top - h - 6)
      const left = Math.min(Math.max(16, anchor.left + anchor.width / 2 - w / 2), Math.max(16, vw - w - 16))
      setPos({ top, left })
    }
    place()
    // the content can grow (the ⋯ menu's hours picker opens inside it) - stay on screen when it does
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver(place)
    ro.observe(el)
    return () => ro.disconnect()
  }, [anchor])
  useEffect(() => {
    const down = e => { if (ref.current && !ref.current.contains(e.target)) onClose() }
    const key = e => { if (e.key === 'Escape') onClose() }
    const scroll = e => { if (!ref.current || !ref.current.contains(e.target)) onClose() }
    // deferred: the tap that opened it must not close it
    const id = setTimeout(() => { document.addEventListener('pointerdown', down); window.addEventListener('scroll', scroll, true) }, 0)
    document.addEventListener('keydown', key)
    return () => { clearTimeout(id); document.removeEventListener('pointerdown', down); window.removeEventListener('scroll', scroll, true); document.removeEventListener('keydown', key) }
  }, [onClose])
  return (
    <div ref={ref} role="dialog" aria-label={label}
      onClick={e => e.stopPropagation()}
      className={`fixed z-[70] max-w-[calc(100vw-2rem)] rounded-lg border border-border bg-popover text-popover-foreground shadow-lg ${className}`}
      style={pos ? { top: pos.top, left: pos.left } : { top: -9999, left: -9999 }}>
      {children}
    </div>
  )
}

function TipPopover({ tip, onClose }) {
  if (!tip) return null
  return (
    <FloatingPanel key={tip.n} anchor={tip.rect} onClose={onClose} label={t('listings_details')} className="w-max max-w-[min(20rem,calc(100vw-2rem))] px-3 py-2 text-xs whitespace-pre-line">
      <div className="flex items-start gap-2" aria-live="polite">
        <div className="min-w-0 flex-1 leading-relaxed break-words">{tip.text}</div>
        <button type="button" onClick={onClose} aria-label={t('listings_close')}
          className={`-mr-1 -mt-0.5 shrink-0 w-6 h-6 flex items-center justify-center rounded text-muted-foreground hover:text-foreground ${TAP}`}>×</button>
      </div>
      {tip.link && (
        <a href={tip.link.href} target="_blank" rel="noopener noreferrer"
           className="mt-1.5 inline-flex items-center min-h-[36px] text-primary font-medium hover:underline">{tip.link.label} ↗</a>
      )}
    </FloatingPanel>
  )
}

// a chip whose title is also readable by a tap
function TipChip({ text, className = '', children }) {
  const show = useTip()
  return (
    <button type="button" title={text} aria-label={text}
      onClick={e => { e.stopPropagation(); show(text, e.currentTarget) }}
      className={`cursor-help text-left ${className}`}>
      {children}
    </button>
  )
}

// THE ONE COLUMN TEMPLATE (owner 2026-11-03). The sticky axis, every section header, every agent row and the "My day"
// rail are all laid out by <Zones>, so each column has the same width at every breakpoint and every hour on the axis sits
// above the same minute on every rail. Five zones, in this order on a wide screen:
//
//   identity (name, dot, check-in, shift) over status (fixed slots) | rail | ⋯ actions over counts + balances
//
// ONE RIGHT COLUMN (owner 2026-11-03, "move these counts below three dots in a column vertically"): the ⋯ and the
// platform counts used to be two columns side by side, 13 rem the rail never got. Now the ⋯ sits on top and the counts
// stack under it, one portal per line, in a column just wide enough for "S 70(43)".
// Below lg (phone AND tablet): identity+status · that same column on line 1, then the rail at the FULL width of the card.
// TRACK FIRST (owner 2026-11-03 "show as long track as possible"): identity and status share one column instead of two,
// there is no separate arrow column (the name is the expand button), and the hour scale stretches to the rail's width
// (buildScale's minW) - every pixel not needed for who / what / how many goes to the timeline.
// A zone never borrows another's space; an empty zone keeps its width.
const ZONE = {
  wrap:     'flex flex-wrap lg:flex-nowrap items-start gap-x-3 gap-y-2',
  who:      'order-1 flex-1 min-w-0 lg:order-none lg:flex-none lg:w-52 flex flex-col gap-0.5',
  // lg: the rail STRETCHES to the row's height (owner 2026-11-03, "why do not we move this line to the bottom"): the right
  // column (⋯ + one portal per line) is often taller than the pins, and the automatic / PC strips sat mid-row above an
  // empty band. Now they run along the row's bottom edge and the rail fills the row.
  rail:     'order-4 w-full min-w-0 lg:order-none lg:w-auto lg:flex-1 lg:self-stretch lg:flex lg:flex-col',
  side:     'order-2 shrink-0 flex flex-col items-end gap-1.5 lg:order-none lg:w-[7.5rem]',   // "S 70(43) 12.50₾" on one line
}
function Zones({ identity = null, status = null, rail = null, counts = null, actions = null, className = '' }) {
  return (
    <div className={`${ZONE.wrap} ${className}`}>
      <div className={ZONE.who}>{identity}{status}</div>
      <div className={ZONE.rail}>{rail}</div>
      <div className={ZONE.side}>{actions}{counts}</div>
    </div>
  )
}

// A number tile - the KPI band, the balance gauges and "My day" all use it
function StatTile({ label, value, tone = null, sub = null, subTone = null, children = null }) {
  return (
    <div className={TILE}>
      <div className="text-xs text-muted-foreground line-clamp-2 leading-snug" title={label}>{label}</div>
      <div className={`text-xl font-bold tabular-nums leading-tight ${tone ? TONE[tone].text : ''}`}>{value ?? '—'}</div>
      {children}
      {sub && <div className={`text-xs ${subTone ? TONE[subTone].text : 'text-muted-foreground'}`}>{sub}</div>}
    </div>
  )
}

// PORTAL BALANCE GAUGE (owner 2026-11-02 "add here platform balances gauge for admin only"): one per paid portal - the
// sum of every agent account's LATEST observed balance, and the bar = share of accounts with money. "Empty" is the same
// <= 0.50 ₾ line that turns a row's balance red. ⚠️ A zero recorded by a failed share stays until the PC or the top-up
// browser reads the account again, so right after a hand top-up the bar can still show it empty.
// + the COMPANY's main account on that portal (core 4160): read by the refill browser every 15 min; older than an hour =
// the reading is failing, shown as a warning so nobody trusts an old number.
// Since 2026-11-03 a tile of the summary band (same shape as the KPI tiles), not a card of its own.
function BalanceGauge({ label, rows, wallet = null }) {
  const n = rows.length
  const empty = rows.filter(b => Number(b.gel) <= 0.5).length
  const total = rows.reduce((s, b) => s + (Number(b.gel) || 0), 0)
  const pct = n ? Math.round(((n - empty) / n) * 100) : 0
  const stale = wallet && (!wallet.read_at || Date.now() - Date.parse(wallet.read_at) > 3600e3)
  const walletLine = wallet ? tLog(stale ? 'listings_bal_wallet_stale' : 'listings_bal_wallet', { b: Number(wallet.balance).toFixed(2),
    t: readAt(wallet.read_at) }) : null
  return (
    <StatTile label={tLog('listings_bal_gauge', { p: label })} value={`${total.toFixed(2)}₾`} tone={empty ? 'bad' : 'ok'}
      sub={walletLine} subTone={stale ? 'warn' : null}>
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 h-2 rounded-full bg-muted overflow-hidden" title={tLog('listings_bal_gauge_sub', { e: empty, f: n - empty, n })}>
          <div className="h-full" style={{ width: `${pct}%`, backgroundColor: TONE.ok.fill }} />
        </div>
        <span className="text-xs text-muted-foreground tabular-nums">{pct}%</span>
      </div>
      <div className="text-xs text-muted-foreground mt-1">{tLog('listings_bal_gauge_sub', { e: empty, f: n - empty, n })}</div>
    </StatTile>
  )
}

// IS THE AGENT'S AUTOMATIC SS / MYHOME LIMITED, AND THE TWO ADMIN BUTTONS (owner 2026-11-02 "admin only two override
// buttons. Reset / Limit with choosing how many hours" + "also show if agent has limit"). `paid` comes from the worker
// switches read, and only for admins (the server leaves it out for anyone else). Limit = stop automatic ss + myhome for N
// hours; Reset = lift every limit on them (absence, fresh cooperations, top-up cap) for N hours; × removes the override.
// The STATE shows inline in the row's status zone; the buttons live in the ⋯ menu.
// ⚠️ window.prompt is gone (owner 2026-11-03, Activity redesign): it silently ignored "abc" or 500 and looked like a
// broken button. The hours are chosen inline - 1 / 3 / 6 / 12 / 24 or a typed number - and a bad number says why.
function paidState(paid) {
  const ov = paid.override
  const until = ov ? fmtTbilisi(ov.until, { timeStyle: 'short' }) : null
  const why = paid.why === 'absent' ? t('listings_paid_absent') : paid.why
  const capped = paid.capped?.length ? tLog('listings_paid_cap', { p: paid.capped.join(', ') }) : null
  const [label, tone, icon] = ov ? [tLog(ov.mode === 'reset' ? 'listings_paid_reset_until' : 'listings_paid_limit_until', { t: until }), ov.mode === 'reset' ? 'ok' : 'warn', ov.mode === 'reset' ? 'check-circle' : 'pause']
    : paid.held ? [t('listings_paid_off'), 'bad', 'ban']
    : capped ? [capped, 'warn', 'alert-triangle']
    : [t('listings_paid_on'), 'ok', 'check-circle']
  const normal = !ov && !paid.held && !capped
  const hint = [label, why, ov ? capped : null].filter(Boolean).join('\n')
  return { label, tone, icon, hint, normal, ov }
}

const PAID_HOURS = [1, 3, 6, 12, 24]
const BTN = `inline-flex items-center justify-center gap-1.5 rounded-md border border-border px-2 h-8 text-xs ${TAP}`
function PaidLimit({ paid, onSet }) {
  const { label, tone, icon, hint, ov } = paidState(paid)
  const [mode, setMode] = useState(null)      // 'limit' | 'reset' while the hours picker is open
  const [custom, setCustom] = useState('')
  const [err, setErr] = useState(null)
  const [busy, setBusy] = useState(false)
  const apply = async h => {
    const n = Number(String(h).replace(',', '.'))
    if (!(n >= 1 && n <= 168)) { setErr(t('listings_paid_hours_invalid')); return }   // the message says 1 to 168
    setBusy(true)
    // the picker closes only when the write landed (onSet answers true) - a failed save keeps the choice on screen
    try { if (await onSet(mode, n)) { setMode(null); setCustom(''); setErr(null) } } finally { setBusy(false) }
  }
  return (
    <div className="space-y-2">
      <div className={`flex items-center gap-1.5 text-xs ${TONE[tone].text}`} title={hint}><Icon name={icon} size={12} />{label}</div>
      {!mode ? (
        <div className="flex flex-wrap gap-2">
          <button type="button" className={`${BTN} hover:bg-accent`} title={t('listings_paid_limit_hint')} onClick={() => { setMode('limit'); setErr(null) }}><Icon name="pause" size={12} />{t('listings_paid_limit')}</button>
          <button type="button" className={`${BTN} hover:bg-accent`} title={t('listings_paid_reset_hint')} onClick={() => { setMode('reset'); setErr(null) }}><Icon name="rotate-ccw" size={12} />{t('listings_paid_reset')}</button>
          {ov && <button type="button" className={`${BTN} text-muted-foreground hover:text-foreground`} onClick={() => onSet('clear', 0)}><Icon name="x" size={12} />{t('listings_paid_clear')}</button>}
        </div>
      ) : (
        <div className="space-y-2">
          <div className="text-xs text-muted-foreground">{tLog('listings_paid_for', { m: t(mode === 'reset' ? 'listings_paid_reset' : 'listings_paid_limit') })}</div>
          <div className="flex flex-wrap gap-2">
            {PAID_HOURS.map(h => (
              <button key={h} type="button" disabled={busy} onClick={() => apply(h)}
                className={`${BTN} tabular-nums hover:bg-accent disabled:opacity-50`}>{h}h</button>
            ))}
          </div>
          <form noValidate className="flex items-center gap-2" onSubmit={e => { e.preventDefault(); apply(custom) }}>
            <input type="number" inputMode="decimal" min="1" max="168" step="any" value={custom}
              onChange={e => { setCustom(e.target.value); setErr(null) }} placeholder={t('listings_paid_custom')}
              aria-label={t('listings_paid_hours')} aria-invalid={!!err}
              className={`w-20 rounded-md border px-2 h-8 text-xs bg-background ${err ? 'border-red-500' : 'border-input'} ${TAP}`} />
            <button type="submit" disabled={busy || !custom} className={`${BTN} bg-primary text-primary-foreground border-primary disabled:opacity-50`}>{t('listings_paid_apply')}</button>
            <button type="button" onClick={() => { setMode(null); setErr(null); setCustom('') }} className={`${BTN} text-muted-foreground`}>{t('listings_paid_cancel')}</button>
          </form>
          {err && <div className={`text-xs ${TONE.bad.text}`} role="alert">{err}</div>}
        </div>
      )}
    </div>
  )
}

// Shared hour axis — labels only where the axis is expanded enough to read.
function TimelineAxis({ scale, nowMinute }) {
  const ticks = []
  // The "now" word sits ON this row, centred on the current minute, so an hour label
  // within half its width lands underneath it — which is how the axis came to read
  // "ახლ24" (owner 2026-08-03). The hour yields, not the marker: an hour label is
  // recoverable from its neighbours, "now" is not. NOW_CLEAR is half the label's widest
  // rendering (Georgian "ახლა" is the long one) plus its padding.
  const NOW_CLEAR = 26
  const nowX = nowMinute != null ? scale.xForMinute(nowMinute) : null
  for (let h = 0; h <= 24; h++) {
    const x = scale.xForMinute(h * 60)
    const next = scale.xForMinute(Math.min(24, h + 1) * 60)
    const span = next - x
    // `left-1` on the label, so its box runs right from the tick — hence the asymmetric
    // window: it can only collide with a marker to its RIGHT.
    const showLabel = (h === 0 || h === 24 || span >= 24)
      && !(nowX != null && x > nowX - NOW_CLEAR - 18 && x < nowX + NOW_CLEAR)
    ticks.push(
      <div key={h} className="absolute top-0 bottom-0" style={{ left: x }}>
        <div className="w-px h-full bg-border/25" />
        {showLabel && (
          // the last label (24) sits LEFT of its tick: to the right it would make the axis wider than the rails
          <div className={`absolute -top-0.5 text-xs text-muted-foreground tabular-nums ${h === 24 ? 'right-1' : 'left-1'}`}>
            {String(h).padStart(2, '0')}
          </div>
        )}
      </div>
    )
  }
  return (
    <div className="relative h-4" style={{ width: scale.totalW }}>
      {ticks}
      {nowMinute != null && (
        <div className="absolute -top-0.5 z-10 flex flex-col items-center -translate-x-1/2"
             style={{ left: nowX }}>
          {/* Its own chip: the hours yield their labels around it (see NOW_CLEAR), but the
              gridlines and the pulsing rail line still pass behind, and red-on-a-hairline
              was reading as smudged. */}
          <span className={`rounded px-1 bg-card text-xs font-semibold leading-none ${TONE.bad.text}`}>{t('listings_now')}</span>
        </div>
      )}
    </div>
  )
}

// THE STICKY AXIS STOPS UNDER THE APP'S TOP BAR (owner 2026-11-03, "overflow bug"): it stuck at top 0 with z-30, over
// App.jsx's own sticky <header> (z-20), and covered the menu while scrolling. It now sticks at the header's bottom edge
// and stays under it. The header's height changes (it wraps on a phone), so it is measured, not assumed.
function useAppHeaderHeight() {
  const [h, setH] = useState(0)
  useEffect(() => {
    const el = document.querySelector('header')
    if (!el) return
    const read = () => setH(el.getBoundingClientRect().height)
    read()
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(read) : null
    ro?.observe(el)
    return () => ro?.disconnect()
  }, [])
  return h
}

// Scrolls a horizontal container once so the "now" marker is in view by default,
// positioned ~60% across (recent past visible, a little future to the right).
// Only fires once so it doesn't fight the user's manual scrolling on each tick.
// ⚠️ `sync` = ONE horizontal position for the axis and every rail (owner 2026-10-20, "timeline issues"): each rail
// scrolled on its own, and once a row's right column changed width (balances, worker chip) every row landed at a
// different scrollLeft - 12:30 on one row sat where 11:00 sat on the next, and the axis read 00-11 above rails showing
// 19:00. Scrolling any of them now moves all of them; a row that mounts later takes the shared position.
function useScrollToNow(scale, nowMinute, sync = null) {
  const ref  = useRef(null)
  const done = useRef(false)
  useEffect(() => {
    const el = ref.current
    if (!el || nowMinute == null || done.current) return
    const nowX = scale.xForMinute(nowMinute)
    el.scrollLeft = sync && sync.left != null ? sync.left : Math.max(0, nowX - el.clientWidth * 0.6)
    done.current = true
  }, [scale, nowMinute, sync])
  // the scale is rebuilt when the rail width is measured (TRACK FIRST) - put every rail back on the shared position
  useEffect(() => {
    const el = ref.current
    if (el && sync && sync.left != null && Math.abs(el.scrollLeft - sync.left) > 1) el.scrollLeft = sync.left
  }, [scale, sync])
  useEffect(() => {
    const el = ref.current
    if (!el || !sync) return
    sync.els.add(el)
    // every rail has the same width (one column template) - report it so the scale can fill it (TRACK FIRST)
    const ro = typeof ResizeObserver !== 'undefined' && sync.setW ? new ResizeObserver(() => { if (el.clientWidth) sync.setW(Math.max(0, el.clientWidth - 2)) }) : null
    ro?.observe(el)
    if (sync.left != null) el.scrollLeft = sync.left
    // an echo (another rail set by this handler) finds every position already equal and writes nothing
    const onScroll = () => {
      sync.left = el.scrollLeft
      for (const o of sync.els) if (o !== el && Math.abs(o.scrollLeft - el.scrollLeft) > 1) o.scrollLeft = el.scrollLeft
    }
    el.addEventListener('scroll', onScroll, { passive: true })
    return () => { el.removeEventListener('scroll', onScroll); sync.els.delete(el); ro?.disconnect() }
  }, [sync])
  return ref
}

// The sticky axis's scroller - a component so it joins the shared position whenever it mounts (see scrollToNow)
function AxisScroll({ scale, nowMinute, sync }) {
  const ref = useScrollToNow(scale, nowMinute, sync)
  return (
    <div ref={ref} className="flex-1 min-w-0 overflow-x-auto">
      <TimelineAxis scale={scale} nowMinute={nowMinute} />
    </div>
  )
}

// Pulsing vertical "now" line, shared style across axis-aligned rows.
function NowLine({ scale, nowMinute }) {
  if (nowMinute == null) return null
  return (
    <div className="absolute top-0 bottom-0 z-20 pointer-events-none"
         style={{ left: scale.xForMinute(nowMinute) }}>
      <div className="w-px h-full opacity-70" style={{ backgroundColor: TONE.bad.fill }} />
      <div className="absolute -top-0.5 -left-[3px] w-[7px] h-[7px] rounded-full animate-ping" style={{ backgroundColor: TONE.bad.fill }} />
      <div className="absolute -top-0.5 -left-[3px] w-[7px] h-[7px] rounded-full" style={{ backgroundColor: TONE.bad.fill }} />
    </div>
  )
}

// height of the automatic-mode and PC strips under a rail (AutoBand / PcBand)
const AUTO_BAND_H = 4
const SHIFT_LABEL_H = 13   // the strip at the top of a rail holding the shift's start / end time
// A day with nothing on it - no post, no shift, no meeting - still gets a rail, so it looks like every other row's
function EmptyRail({ scale }) {
  return (
    <div className="relative bg-muted/20 rounded-md flex items-center text-xs text-muted-foreground lg:flex-1" style={{ width: scale.totalW, minHeight: 22 }}>
      {/* sticky: the empty rail scrolls with the others, the words stay in view */}
      <span className="sticky left-0 px-2">{t('listings_no_activity')}</span>
    </div>
  )
}

// a pin's facts - the hover title, the tap popover and the "My day" failure list read this one text
function pinTitle(ev, autoIds) {
  const outcome = outcomeOf(ev)
  return `${fmtClock(ev.created_at)} · ${PLATFORM_LABEL[ev.platform] || ev.platform}`
    + `${ev.deal_type ? ' · ' + dealLabel(ev.deal_type) : ''}`
    + ` · ${t(OUTCOME_LABEL[outcome])}${outcome === 'failed' && ev.fail_reason ? ` (${ev.fail_reason})` : ''}`
    /* Facebook is ONE dot per share session (owner 2026-08-20) — the hover carries
       how far the posting spree actually reached; other posts findable by #id. */
    + (ev.post_count > 1 ? `\n${ev.post_count} posts${ev.group_count ? ` · ${ev.group_count} groups` : ''}` : '')
    + (isAutoPost(ev, autoIds) ? `\n⟳ ${t('listings_legend_auto')}` : '')
}
const pinLink = ev => (ev.published && ev.offer_url ? { href: ev.offer_url, label: t('listings_open_advert') } : null)

// A COOPERATION OPENED (owner 2026-11-03, "add cooperation opening on the timeline … clicking on it opening lead"): a
// small cyan DIAMOND at the minute the agent opened it (core GET /cooperations/opened). Not a dot - dots are posts and
// their colour is the portal - and not a vertical line - the rail already has the now line, shift edges, meeting stems
// and hour gridlines. Same size as a pin, drawn UNDER the pins like the meetings (a post must never be hidden), in the
// row of its own under the pins (AgentTimeline's coopPlaced). A mouse click opens the lead (the cooperation's source listing) in a new tab, as a pin opens its
// advert; a tap shows the time with an "Open lead" link.
const coopLink = c => (c.listing_id ? { href: `${window.location.origin}/#datadesk/listing/${encodeURIComponent(c.listing_id)}`, label: t('listings_open_lead') } : null)
const coopText = c => tLog('listings_coop_tip', { t: fmtTbilisi(c.at, { hour: '2-digit', minute: '2-digit', hour12: false }),
  i: c.intent ? dealLabel(c.intent) : '' }).replace(/ · $/, '')
function CoopMarks({ coops, top = 0 }) {
  const show = useTip()
  return coops.map(c => {
    const x = c.x
    const link = coopLink(c)
    const open = e => {
      e.stopPropagation()
      if (isCoarse() || !link) { show(coopText(c), { x: e.clientX, y: e.clientY }, link); return }
      window.open(link.href, '_blank', 'noopener')
    }
    return (
      <button key={c.id} type="button" title={`${coopText(c)}${link ? `\n${link.label}` : ''}`} aria-label={coopText(c)} onClick={open}
        className="absolute z-[5] flex items-center justify-center w-[13px] h-[13px] -translate-x-1/2 cursor-pointer hover:scale-125 transition-transform focus:outline-none focus-visible:ring-2 focus-visible:ring-primary"
        style={{ left: x, top: top + c.lane * 12 }}>
        <span className="block w-[8px] h-[8px] rotate-45 rounded-[1px] border" style={{ backgroundColor: COOP_FILL, borderColor: COOP_EDGE }} />
      </button>
    )
  })
}

// One agent's pins on the shared scale. `shifts` = scheduled work slots
// [{ start:'HH:MM', end:'HH:MM', location }]; `nowMinute` draws the live marker.
function AgentTimeline({ events, scale, shifts = [], nowMinute, meetings = [], nudgeTiers = null, autoIds = null, autoLog = null, pcLog = null, pcGaps = null, coops = null }) {
  const LANE_H = 13      // vertical spacing between stacked pins
  const MAX_LANES = 5
  // Meetings sit CENTERED on the rail (owner 2026-08-01), not in a reserved top
  // strip: an appointment is the anchor of the day, so it reads as a landmark on the
  // track rather than a footnote above it. That means it can land on a pin — hence
  // the larger glyph and the z-order below, and no extra row height.

  // Assign vertical lanes so clustered pins don't fully overlap.
  const placed = useMemo(() => {
    const PIN = 12          // pin footprint (px)
    const sorted = [...events].sort((a, b) => minuteOfDay(a.created_at) - minuteOfDay(b.created_at))
    const laneLastX = []
    return sorted.map(ev => {
      const x = scale.xForMinute(minuteOfDay(ev.created_at))
      let lane = laneLastX.findIndex(lx => x - lx >= PIN)
      if (lane === -1) {
        if (laneLastX.length < MAX_LANES) { lane = laneLastX.length; laneLastX.push(x) }
        else { lane = 0; laneLastX[0] = x }
      } else { laneLastX[lane] = x }
      return { ev, x, lane }
    })
  }, [events, scale])

  const lanesUsed = placed.reduce((m, p) => Math.max(m, p.lane + 1), 1)
  // ⚠️ The rail is `overflow-hidden`, so a marker taller than the row is CLIPPED, not
  // overflowed — a row with meetings must be at least as tall as the glyph or the
  // trophy loses its top and bottom.
  const pcH = pcLog?.length || pcGaps?.length ? AUTO_BAND_H + 1 : 0
  // the shift's start / end times get a strip of their own at the top (review 2026-11-03): at the bottom they sat under
  // the automatic / PC strips and the pins of the first lane, and 08:00 / 10:00 could not be read
  const labelH = shifts.length ? SHIFT_LABEL_H : 0
  // COOPERATION DIAMONDS GET THEIR OWN ROW under the pin lanes (owner 2026-11-03 "make sure the dots don't collide"):
  // drawn mid-rail they sat on top of a busy hour's pins. Two diamonds closer than a diamond's width take a second line.
  const pinsH = Math.max(meetings.length ? MEETING_ICON_PX + 4 : 22, lanesUsed * LANE_H + 6)
  const coopPlaced = useMemo(() => {
    const last = []
    return (coops || []).map(c => {
      const x = scale.xForMinute(c.minute)
      let lane = last.findIndex(lx => x - lx >= 12)
      if (lane === -1) lane = last.length < 2 ? last.length : 0
      last[lane] = x
      return { ...c, x, lane }
    })
  }, [coops, scale])
  const coopH = coopPlaced.length ? (Math.max(...coopPlaced.map(c => c.lane)) + 1) * 12 + 2 : 0
  const height = labelH + pinsH + coopH + (autoLog?.length ? AUTO_BAND_H + 2 : 0) + pcH

  // TAP ON THE RAIL (owner 2026-11-03, Activity redesign): a 13 px pin, a 4 px band and a meeting glyph all carried their
  // facts in a hover-only title. A tap anywhere on the rail now says what was there: the nearest pin (with its advert
  // link), or the meeting, shift, automatic mode and PC state at that minute. A mouse click on a published pin still
  // opens the advert directly (the pin's own handler).
  const show = useTip()
  const onRailClick = e => {
    if (e.target.closest('button')) return
    const rect = e.currentTarget.getBoundingClientRect()
    const x = e.clientX - rect.left
    const reach = isCoarse() ? 18 : 8
    const pin = placed.reduce((best, p) => (Math.abs(p.x - x) <= reach && (!best || Math.abs(p.x - x) < Math.abs(best.x - x)) ? p : best), null)
    const point = { x: e.clientX, y: e.clientY }
    if (pin) { show(pinTitle(pin.ev, autoIds), point, pinLink(pin.ev)); return }
    show(describeMinute(scale.minuteForX(x), { shifts, meetings, autoLog, pcLog, pcGaps, scale, x }), point)
  }

  return (
    // overflow-CLIP, not hidden: it clips the same way but is not a scroll container, so the shift labels' `sticky` binds
    // to the rail's scroller and a label stays readable at the edge instead of being cut ("0" of 10:00 on a phone)
    // `isolate` (owner 2026-11-03, "this red line overflow"): the now line (z-20), the pins (z-10) and the diamonds are
    // z-indexed INSIDE the rail; without a stacking context of its own those numbers competed with the page's, and the now
    // line painted over App.jsx's sticky header (also z-20, earlier in the page) and the sticky hour axis as rows scrolled
    // under them. Isolated, the rail is one layer and every sticky bar above it covers it whole.
    <div className="relative isolate bg-muted/20 rounded-md overflow-clip cursor-pointer lg:flex-1"
         onClick={onRailClick}
         style={{ width: scale.totalW, minHeight: height }}>
      {/* ⚠️ Everything except the now line is a MEMO layer (audit 2026-10-21): the clock ticks every 30 s and used to
          re-render ~2,500 pins on every rail with it. Only NowLine depends on nowMinute. */}
      <RailMarks placed={placed} scale={scale} shifts={shifts} meetings={meetings} nudgeTiers={nudgeTiers} autoIds={autoIds} laneH={LANE_H} top={labelH} />
      {autoLog?.length > 0 && <AutoBand log={autoLog} scale={scale} nowMinute={nowMinute} bottom={pcH} />}
      {pcLog?.length > 0 && <PcBand log={pcLog} scale={scale} nowMinute={nowMinute} />}
      {pcGaps?.length > 0 && <GapMarks gaps={pcGaps} scale={scale} />}
      {coopPlaced.length > 0 && <CoopMarks coops={coopPlaced} top={labelH + pinsH} />}
      <NowLine scale={scale} nowMinute={nowMinute} />
    </div>
  )
}

// WHEN AUTOMATIC MODE WAS ON (owner 2026-10-28 "instead of circle dots to denote it's auto-reshared we should have lines
// below that tell us when auto mode was on and when off"): a strip along the rail's bottom from core 3995's
// fb.agent_auto_log (worker-switches → states[login].auto_log) - violet while automatic shares could run, grey while
// paused (the agent's switch, or in the office under 'auto'); up to now, never into the future. Before 2026-10-28 00:50
// only the current switch was stored, so earlier days have no strip.
const hmOf = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.floor(m % 60)).padStart(2, '0')}`
// the state a time-ordered on/off log was in at minute m (the last change at or before it), or null
function logAt(log, m) {
  let cur = null
  for (const r of log || []) { if (minuteOfDay(r.at) <= m) cur = r; else break }
  return cur
}
function autoWhy(s) {
  return s.auto ? t('listings_auto_on') : s.mode === 'paused' ? t('listings_auto_off_paused') : s.in_office ? t('listings_auto_off_office') : t('listings_auto_off')
}
// what the rail says at one minute - the same words the hover titles use
function describeMinute(m, { shifts = [], meetings = [], autoLog = null, pcLog = null, pcGaps = null, scale, x }) {
  const lines = [hmOf(m)]
  const near = meetings.find(mt => Math.abs(scale.xForMinute(mt.minute) - x) <= (isCoarse() ? 20 : 16))
  if (near) lines.push(near.title)
  const hm = v => String(v).replace(/^(\d{1,2}:\d{2}):\d{2}$/, '$1')
  const sh = shifts.find(s => { const a = timeToMinutes(s.start), b = timeToMinutes(s.end); return a != null && b != null && m >= a && m <= b })
  if (shifts.length) lines.push(sh ? tLog('listings_rail_shift', { t: `${hm(sh.start)}–${hm(sh.end)}` }) : t('listings_rail_no_shift'))
  const a = logAt(autoLog, m)
  if (a) lines.push(autoWhy(a))
  const pc = logAt(pcLog, m)
  if (pc) lines.push(pc.online ? t('listings_pc_online') : t('listings_pc_offline'))
  const reach = isCoarse() ? 18 : 8
  for (const g of pcGaps || []) {
    const x1 = scale.xForMinute(minuteOfDay(g.from)), x2 = scale.xForMinute(minuteOfDay(g.to))
    if (x >= x1 - reach && x <= x2 + reach) lines.push(gapText(g))
  }
  return lines.join('\n')
}
function AutoBand({ log, scale, nowMinute, bottom = 0 }) {
  const end = nowMinute ?? 24 * 60
  const segs = []
  for (let i = 0; i < log.length; i++) {
    const a = Math.max(0, minuteOfDay(log[i].at))
    const b = i + 1 < log.length ? minuteOfDay(log[i + 1].at) : end
    if (b > a) segs.push({ a, b, ...log[i] })
  }
  const hm = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.floor(m % 60)).padStart(2, '0')}`
  return segs.map((s, i) => {
    const x1 = scale.xForMinute(s.a), x2 = scale.xForMinute(s.b)
    const why = autoWhy(s)
    return <div key={i} className="absolute" title={`${hm(s.a)}–${hm(s.b)} · ${why}`}
      style={{ bottom, left: x1, width: Math.max(1, x2 - x1), height: AUTO_BAND_H, backgroundColor: s.auto ? AUTO_FILL : TONE.muted.fill }} />
  })
}

// WHEN THE AGENT'S PC WAS ON / OFF LINE (owner 2026-11-01 "also below the auto mode line show pc on off line"): core 4122's
// fb.ext_pc_log (worker-switches → states[login].pc_log) - green while any of the agent's browsers checked in, red while
// none did - drawn under the automatic-mode strip, so "auto was on but the PC was off" reads at a glance. The log starts on
// 2026-11-01; earlier days have no strip.
function PcBand({ log, scale, nowMinute }) {
  const end = nowMinute ?? 24 * 60
  const hm = m => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(Math.floor(m % 60)).padStart(2, '0')}`
  return log.map((r, i) => {
    const a = Math.max(0, minuteOfDay(r.at)), b = i + 1 < log.length ? minuteOfDay(log[i + 1].at) : end
    if (!(b > a)) return null
    const x1 = scale.xForMinute(a), x2 = scale.xForMinute(b)
    return <div key={i} className="absolute bottom-0" title={`${hm(a)}–${hm(b)} · ${r.online ? t('listings_pc_online') : t('listings_pc_offline')}`}
      style={{ left: x1, width: Math.max(1, x2 - x1), height: AUTO_BAND_H, backgroundColor: r.online ? TONE.ok.fill : TONE.bad.fill }} />
  })
}

// A BROWSER'S SHORT SILENCE (owner 2026-11-03, "she restarted pc and there is no evidence it was restarted"): the PC strip
// above only turns red after 3 minutes with no browser, so a restart left no mark. The API keeps every silence of one browser
// over 2 minutes (ext_sessions.js `gaps`, in memory - an API restart forgets them) and they are drawn here as red notches,
// taller than the strip so they read even inside a green stretch kept "on" by another browser. Today only.
const hhmm = iso => fmtTbilisi(iso, { hour: '2-digit', minute: '2-digit', hour12: false })
const gapText = g => tLog('listings_pc_gap', { a: hhmm(g.from), b: hhmm(g.to), d: g.device || '?' })
function GapMarks({ gaps, scale }) {
  return gaps.map((g, i) => {
    const x1 = scale.xForMinute(minuteOfDay(g.from)), x2 = scale.xForMinute(minuteOfDay(g.to))
    return <div key={i} className="absolute bottom-0 rounded-t-sm" title={gapText(g)}
      style={{ left: Math.min(x1, x2 - 3), width: Math.max(3, x2 - x1), height: AUTO_BAND_H + 5, backgroundColor: TONE.bad.fill }} />
  })
}
// the silences inside the day on screen - only today has them (the API remembers the last 24 hours)
function todaysGaps(gaps, nowMinute) {
  if (nowMinute == null || !gaps?.length) return null
  const list = gaps.filter(g => Date.now() - Date.parse(g.from) <= nowMinute * 60e3)
  return list.length ? list : null
}

const SHIFT_TWO_LABELS_PX = 84   // two "19:00" labels and a gap
const hmShort = v => String(v).replace(/^(\d{1,2}:\d{2}):\d{2}$/, '$1')

const RailMarks = memo(function RailMarks({ placed, scale, shifts, meetings, nudgeTiers, autoIds, laneH, top = 0 }) {
  const show = useTip()
  return (
    <>
      {/* scheduled shift band(s) with start/end markers */}
      {shifts.map((s, i) => {
        const sm = timeToMinutes(s.start), em = timeToMinutes(s.end)
        if (sm == null || em == null || em <= sm) return null
        const x1 = scale.xForMinute(sm), x2 = scale.xForMinute(em)
        const { tint, edge } = SHIFT_FILL[s.location === 'home' ? 'home' : 'office']
        return (
          <div key={i} className="absolute top-0 bottom-0" style={{ left: x1, width: Math.max(2, x2 - x1) }}
               title={`${s.start}–${s.end}`}>
            <div className="absolute inset-0" style={{ backgroundColor: tint }} />
            <div className="absolute top-0 bottom-0 left-0 w-px" style={{ backgroundColor: edge }} />
            <div className="absolute top-0 bottom-0 right-0 w-px" style={{ backgroundColor: edge }} />
            {/* start / end in the label strip at the top; sticky so a label stays inside the visible rail.
                ⚠️ A band too narrow for two labels (a shift in quiet, compressed hours - 15:30-19:00 can be ~50 px) printed
                them on top of each other ("1519", owner 2026-11-03): it gets ONE label, "15:30–19:00", instead. */}
            {x2 - x1 < SHIFT_TWO_LABELS_PX
              ? <div className="absolute left-0 top-0 pointer-events-none whitespace-nowrap" style={{ height: SHIFT_LABEL_H }}>
                  <span className="px-0.5 text-xs leading-none text-muted-foreground tabular-nums bg-muted/60 rounded-sm">{hmShort(s.start)}–{hmShort(s.end)}</span>
                </div>
              : <div className="absolute inset-x-0 top-0 flex justify-between gap-1 pointer-events-none" style={{ height: SHIFT_LABEL_H }}>
                  <span className="sticky left-0 px-0.5 text-xs leading-none text-muted-foreground tabular-nums bg-muted/60 rounded-sm">{hmShort(s.start)}</span>
                  <span className="sticky right-0 px-0.5 text-xs leading-none text-muted-foreground tabular-nums bg-muted/60 rounded-sm">{hmShort(s.end)}</span>
                </div>}
          </div>
        )
      })}
      {/* faint hour gridlines — kept very muted so they don't compete with the pins */}
      {Array.from({ length: 25 }, (_, h) => (
        <div key={h} className="absolute top-0 bottom-0 w-px bg-border/[0.08]"
             style={{ left: scale.xForMinute(h * 60) }} />
      ))}
      {/* Meetings — glyph centered on the rail, on a hairline stem so the exact minute
          stays readable against the pins and the shift band.
          ⚠️ BEHIND the pins since 2026-08-03 (owner: "listings markers must not be hidden
          under the meetings markers"). A meeting is a PERIOD and a posting is a MOMENT: the
          period is context for the day, the moments are the day's record, so the period
          belongs in the background layer. A 30px glyph drawn on top was swallowing whole
          clusters of pins — and the case where it mattered most is exactly the case the
          owner named: a meeting that fell through while the agent kept posting. */}
      {meetings.map((mt, i) => {
        const x = scale.xForMinute(mt.minute)
        const dim = MEETING_OPACITY[mt.status]
        // 0427 (designer 1a): the OFFENDING glyph wears a ring in its tier's
        // colour, so "which hour" is answered without a second element. A
        // cancelled or moved meeting is already faint and owes nothing — it can
        // never be in the map, so absence stays legible.
        const nudgeTier = nudgeTiers?.get(mt.id) || null
        const ring = nudgeTier ? NUDGE_FILL[nudgeTier] : null
        return (
          <div key={mt.id || i} className="absolute bottom-0 z-0 pointer-events-none"
               style={{ top, left: x, opacity: dim ?? 1 }}>
            <div className={`w-px h-full ${mt.status === 'moved' ? 'bg-foreground/10' : 'bg-foreground/15'}`} />
            <span title={mt.title} role="img" aria-label={mt.title}
                  className={`absolute top-1/2 -translate-x-1/2 -translate-y-1/2 leading-none select-none cursor-default pointer-events-auto ${
                    MEETING_TONE[mt.status] || 'text-muted-foreground'} ${ring ? 'rounded-full p-0.5' : ''}`}
                  style={ring ? { boxShadow: `0 0 0 2px ${ring}` } : undefined}>
              <Icon name={MEETING_ICON[mt.status] || 'calendar'} size={MEETING_ICON_PX} />
            </span>
          </div>
        )
      })}
      {placed.map(({ ev, x, lane }, i) => {
        const color = PIN_COLOR[ev.platform] || '#9ca3af'
        const clickable = ev.published && ev.offer_url
        // Four outcomes (outcomeOf): published (filled dot) · failed (red ✕) · filled-not-published / pending (hollow)
        const outcome = outcomeOf(ev)
        const failedEv = outcome === 'failed'
        // AUTOMATIC post (owner 2026-10-19: "use the ring for automatic posts and change color too"): the extension posted it
        // on its own - a scheduled / pool / cooperation run or an edit (its advert id is among share_plans' published runs)
        const title = pinTitle(ev, autoIds) + (clickable ? `\n${t('listings_open_advert')}` : '')
        // ⚠️ A real <button> (audit 2026-10-21): the dots were bare divs - no keyboard focus, no accessible name, a
        // 9 px target. The button is the lane's full 13 px square, the visible dot stays 9 px so lanes don't move.
        return (
          <button type="button"
            key={ev.id || i}
            title={title}
            aria-label={title}
            /* touch: a tap shows the pin's facts first (with the advert link) - a phone has no hover to read them by */
            onClick={e => {
              if (clickable && !isCoarse()) { window.open(ev.offer_url, '_blank', 'noopener'); return }
              show(pinTitle(ev, autoIds), e.currentTarget, pinLink(ev))
            }}
            /* z-10: pins always draw ON TOP of the meeting layer — see the meeting block. */
            className={`absolute z-10 flex items-center justify-center rounded-full transition-transform hover:scale-125 focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${clickable ? 'cursor-pointer' : 'cursor-default'}`}
            style={{ left: x - 6.5, top: top + (failedEv ? 0 : 2) + lane * laneH, width: 13, height: 13 }}
          >
            {failedEv
              ? <span className="leading-none select-none inline-flex" style={{ color }}><Icon name="x" size={12} /></span>
              : <span className="block rounded-full border" style={{
                  width: 9, height: 9,
                  backgroundColor: outcome === 'published' ? color : 'transparent',
                  borderColor: color,
                  // ⚠️ no violet ring since 2026-10-28 (owner): automatic mode is the strip under the rail (AutoBand);
                  // a single post's "automatic" stays in its hover text
                }} />}
          </button>
        )
      })}
    </>
  )
})

// ─── the STATUS zone: fixed slots ─────────────────────────────────────────────
// Fixed order, every row: PC · auto-update · version · access & automation (lock, limit, pauses), as one compact line
// under the name (they share the left column so the rail keeps its width); a slot with nothing to say takes no room. Which slots exist is decided once for the page
// (`slots`): PC and auto-update only when the worker read came back (managers / admins, or the agent's own card).
const SLOT = 'min-h-5 flex items-center min-w-0 max-w-full'

function pcInfo(pc) {
  const seen = pc?.seen_at ? fmtTbilisi(pc.seen_at, { hour: '2-digit', minute: '2-digit', hour12: false }) : null
  const [text, tone] = !pc
    ? [t('listings_pc_unknown'), 'muted']
    : pc.online && pc.job
      ? [tLog('listings_pc_working', { p: PLATFORM_LABEL[pc.job.target] || pc.job.target }), 'ok']
      : pc.online
        ? [t('listings_pc_idle'), 'ok']
        : [tLog('listings_pc_seen', { t: seen }), 'warn']
  return { text, tone, hint: seen ? tLog('listings_pc_seen', { t: seen }) : t('listings_pc_unknown') }
}
// ⚠️ "on" = ANY of the agent's browsers checked in within 3 minutes (ext_sessions.js pcStateFor) - a second PC, a laptop at
// home or another machine signed in as them keeps it "on" while their own PC is off (owner 2026-11-03: "barbare's pc is off
// and it shows on"). So the tap text names every browser with its state, and the short value counts them when more than one
// is on line.
function pcDevicesText(pc) {
  return (pc?.devices || []).map(d => `${d.online ? '●' : '○'} ${d.name || '?'}${d.ext_version ? ` · v${d.ext_version}` : ''} · ${d.online ? t('listings_pc_short_on') : t('listings_pc_offline')}`).join('\n')
}
function PcSlot({ pc, full = false }) {
  const { text, tone, hint } = pcInfo(pc)
  const onCount = (pc?.devices || []).filter(d => d.online).length
  // short value: what it is doing (SS / idle) or since when it is off line - the sentence is in the tap text
  const short = (!pc ? '?' : pc.online && pc.job ? (PLATFORM_SHORT[pc.job.target] || pc.job.target) : pc.online ? t('listings_pc_short_on') : (pc.seen_at ? fmtTbilisi(pc.seen_at, { hour: '2-digit', minute: '2-digit', hour12: false }) : '—'))
    + (onCount > 1 ? ` ×${onCount}` : '')
  const devText = pcDevicesText(pc)
  return (
    <TipChip text={`${text}${hint !== text ? '\n' + hint : ''}${devText ? '\n\n' + devText : ''}`} className={`inline-flex items-center gap-1 min-w-0 max-w-full ${TONE[tone].text}`}>
      <span className="inline-block w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: TONE[tone].fill }} aria-hidden="true" />
      <span className="truncate">{full ? text : short}</span>
    </TipChip>
  )
}
// The PC's auto-updater (owner 2026-10-27): ok = installs new builds by itself, warn = installed but its last run failed
// (reason on hover / tap), muted = not set up (the agent runs install-autoupdate.bat / .sh once). Nothing until the PC
// has reported - a fresh API restart knows nothing yet.
function UpdaterSlot({ au, full = false }) {
  if (!au) return null
  const [label, tone, hint] = au.on && !au.error
    ? [t('listings_au_on'), 'ok', t('listings_au_on_hint')]
    : au.on
      ? [t('listings_au_error'), 'warn', `${t('listings_au_error_hint')}: ${au.error}`]
      : [t('listings_au_off'), 'muted', t(au.kind === 'mac' ? 'listings_au_off_hint_mac' : 'listings_au_off_hint_win')]
  return <TipChip text={`${label} · ${hint}`} className={`inline-flex items-center gap-1 min-w-0 ${TONE[tone].text}`}><Icon name="refresh-cw" size={12} />{full && <span className="truncate">{label}</span>}</TipChip>
}
// App version: ok = on latest, warn = outdated, muted = never seen.
function VersionSlot({ info, latestVersion }) {
  if (!info || !info.app_version) {
    return <TipChip text={t('listings_ver_unknown')} className={`inline-flex items-center gap-1 ${TONE.muted.text}`}><Icon name="puzzle" size={12} />—</TipChip>
  }
  const upToDate = !latestVersion || info.app_version === latestVersion
  const seen = info.last_seen ? `${t('listings_ver_lastseen')}: ${new Date(info.last_seen).toLocaleString()}` : ''
  return (
    <TipChip text={`${info.app_version} · ${upToDate ? t('listings_ver_uptodate') : t('listings_ver_outdated')}${seen ? '\n' + seen : ''}`}
      className={`inline-flex items-center gap-1 tabular-nums ${TONE[upToDate ? 'ok' : 'warn'].text}`}>
      <Icon name={upToDate ? 'puzzle' : 'alert-triangle'} size={12} />{'v' + String(info.app_version).split('.').slice(0, 2).join('.')}
    </TipChip>
  )
}
// lock (access revoked), the paid limit when it is not the normal state, and a paused 🔁 / 💬 part - each says itself only
// when it is not normal, so a calm row has an empty slot here
function AccessSlot({ locked, paid, worker }) {
  const ps = paid ? paidState(paid) : null
  const paused = worker ? [['reshare', 'reshare_off', 'repeat'], ['wa', 'wa_off', 'message-circle']].filter(([, k]) => worker[k]) : []
  if (!locked && !(ps && !ps.normal) && !paused.length) return null
  return (
    <span className="inline-flex items-center gap-1.5 min-w-0 max-w-full">
      {locked && <TipChip text={`${t('listings_locked')} · ${t('listings_locked_hint')}`} className={`inline-flex items-center ${TONE.bad.text}`}><Icon name="lock" size={12} /></TipChip>}
      {paused.map(([part, , icon]) => (
        <TipChip key={part} text={`${t(`my_worker_part_${part}`)} · ${t('my_worker_part_off')}`} className={`inline-flex items-center ${TONE.warn.text}`}>
          <Icon name={icon} size={12} /><Icon name="pause" size={10} />
        </TipChip>
      ))}
      {ps && !ps.normal && <TipChip text={ps.hint} className={`inline-flex items-center ${TONE[ps.tone].text}`}><Icon name={ps.icon} size={12} /></TipChip>}
    </span>
  )
}
function StatusSlots({ slots, worker, versionInfo, latestVersion, locked = false, paid = null }) {
  const node = s => s === 'pc' ? <PcSlot pc={worker?.pc || null} />
    : s === 'updater' ? <UpdaterSlot au={worker?.pc?.auto_update} />
    : s === 'version' ? <VersionSlot info={versionInfo} latestVersion={latestVersion} />
    : <AccessSlot locked={locked} paid={paid} worker={worker} />
  return (
    <div className="flex flex-wrap items-center gap-x-2.5 gap-y-0.5 text-xs">
      {slots.map(s => { const n = node(s); return n ? <span key={s} className={SLOT}>{n}</span> : null })}
    </div>
  )
}

// trade/lease as stored → the reader's words; an unknown value shows as stored
function dealLabel(v) {
  return tOr(`listings_deal_${String(v).toLowerCase()}`, String(v))
}

const HISTORY_PAGE = 50

// One outcome count per row - the row and "My day" read the same numbers
function countOutcomes(events) {
  const c = { published: 0, failed: 0, filled: 0, pending: 0 }
  const perPlat = {}
  for (const e of events) {
    const o = outcomeOf(e)
    c[o]++
    if (!e.platform) continue
    if (!perPlat[e.platform]) perPlat[e.platform] = { pub: 0, fail: 0 }
    if (o === 'published') perPlat[e.platform].pub++
    else if (o === 'failed') perPlat[e.platform].fail++
  }
  return { ...c, perPlat }
}

// ⚠️ every platform the dots can carry - 'home' was missing, so a row's tallies could add up short of its total
const TALLY_ORDER = ['korter', 'myhome', 'ss', 'home', 'facebook', 'facebook_group']

// THE DAY'S TOTAL PER PORTAL, inside the Published / Failed summary tiles (owner 2026-11-03 "also add here each platforms
// counts"): the same coloured pills as each row's counts, in the same order, so the tile reads K 120 · M 380 · S 500 …
function PlatformSplit({ perPlat, field }) {
  const items = TALLY_ORDER.filter(p => perPlat[p]?.[field] > 0)
  if (!items.length) return null
  return (
    <div className="flex flex-wrap gap-1 mt-0.5">
      {items.map(p => (
        <TipChip key={p} text={`${PLATFORM_LABEL[p]}: ${perPlat[p][field]}`}
              className={`${COUNTER} font-semibold tabular-nums ${PLATFORM_COLOR[p] || 'bg-muted text-muted-foreground'}`}>
          {PLATFORM_LETTER[p] || p} {perPlat[p][field]}
        </TipChip>
      ))}
    </div>
  )
}
const balanceAt = b => readAt(b?.at)

// The COUNTS zone: "published (failed)" per platform in a fixed order so they line up across agents, and the account's
// balance on that portal UNDER its counter (owner 2026-10-20: "instead of putting balance after it put below it") - red
// at 0.50 ₾ or less. ⚠️ It is the LATEST observed balance, whatever day is on screen - the tap / title says so and when.
function Tallies({ perPlat, balances, coops = 0 }) {
  const items = TALLY_ORDER.filter(p => perPlat[p])
  if (!items.length && !coops) return null
  // ONE PORTAL PER LINE under the ⋯ on a wide screen (owner 2026-11-03), in the fixed TALLY_ORDER so K is always above M
  // above S, and the balance right AFTER its counter on the same line (owner 2026-11-03 "write balances after platform" -
  // it was under it since 2026-10-20). On lg the lines are a two-column grid, counter | balance, so every counter's right
  // edge and every balance's left edge line up down the column whether or not a portal has a balance.
  // Below lg they wrap side by side: stacked, six portals made a phone row 150 px tall with an empty gap under the name.
  return (
    <div className="flex flex-wrap justify-end gap-1 max-w-[10rem] lg:max-w-none lg:grid lg:grid-cols-[auto_auto] lg:gap-x-1 lg:gap-y-1 lg:items-center">
      {items.map(p => {
        const { pub, fail } = perPlat[p]
        const bal = balances?.[p]
        return (
          <span key={p} className="inline-flex items-center gap-1 whitespace-nowrap lg:contents">
            <TipChip text={`${PLATFORM_LABEL[p]}: ${tLog('listings_counts_compact', { p: pub, f: fail })}`}
                  className={`${COUNTER} font-semibold tabular-nums lg:justify-self-end ${PLATFORM_COLOR[p] || 'bg-muted text-muted-foreground'}`}>
              {PLATFORM_LETTER[p] || p}{' '}{pub}
              {fail > 0 && <span className={TONE.bad.text}>({fail})</span>}
            </TipChip>
            {bal == null && <span className="hidden lg:block" aria-hidden="true" />}
            {bal != null && (
              <TipChip text={`${PLATFORM_LABEL[p]} ${Number(bal.gel).toFixed(2)}₾ · ${tLog('listings_balance_latest', { t: balanceAt(bal) })}`}
                    className={`text-xs tabular-nums ${bal.gel <= 0.5 ? `${TONE.bad.text} font-semibold` : 'text-muted-foreground'}`}>
                {Number(bal.gel).toFixed(2)}₾
              </TipChip>
            )}
          </span>
        )
      })}
      {/* the day's cooperations opened, after the portals - the same diamond as on the rail */}
      {coops > 0 && <>
        <TipChip text={tLog('listings_coop_count', { n: coops })}
              className={`${COUNTER} font-semibold tabular-nums lg:justify-self-end inline-flex items-center gap-1 bg-cyan-500/15 text-cyan-700 dark:text-cyan-300`}>
          <span className="inline-block w-[7px] h-[7px] rotate-45 rounded-[1px] border" style={{ backgroundColor: COOP_FILL, borderColor: COOP_EDGE }} aria-hidden="true" />{coops}
        </TipChip>
        <span className="hidden lg:block" aria-hidden="true" />
      </>}
    </div>
  )
}

// THE ⋯ MENU (owner 2026-11-03, Activity redesign): lock, 🔁, 💬 and the admin's Limit / Reset used to sit as chips under
// every name, so a manager's page was a wall of buttons. They live behind one ⋯ per row now (the ACTIONS zone); the row's
// status zone keeps the facts. Gating is unchanged: lock = `listings.lock`, the two switches only when the worker-switches
// read came back (the server answers 403 to agents), Limit / Reset admin only.
function RowActionsMenu({ agentLabel, locked, onToggleLock, worker, onSetWorkerPart, onPaidOverride }) {
  const [rect, setRect] = useState(null)
  // locking asks inline, like Limit (review 2026-11-03): the consequences, an optional reason, Confirm / Cancel - no
  // browser confirm() / prompt(). The menu closes only when the write landed.
  const [lockAsk, setLockAsk] = useState(false)
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const close = useCallback(() => { setRect(null); setLockAsk(false); setReason('') }, [])
  const doLock = async (r) => {
    setBusy(true)
    try { if (await onToggleLock(r || null)) close() } finally { setBusy(false) }
  }
  const switches = worker && onSetWorkerPart
  const paid = worker?.paid && onPaidOverride
  if (!onToggleLock && !switches && !paid) return null
  const item = `w-full flex items-center gap-2 rounded-md px-2 h-9 text-sm text-left hover:bg-accent ${TAP}`
  return (
    <>
      <button type="button" aria-haspopup="menu" aria-expanded={!!rect} aria-label={`${t('listings_actions')}: ${agentLabel}`} title={t('listings_actions')}
        /* pointerdown stops here, or the panel's own outside-tap close would fire first and this click reopen it */
        onPointerDown={e => e.stopPropagation()}
        onClick={e => { e.stopPropagation(); const box = e.currentTarget.getBoundingClientRect(); setRect(r => (r ? null : box)) }}  /* read the box NOW: currentTarget is gone by the time a deferred updater runs */
        className={`inline-flex items-center justify-center w-8 h-8 rounded-md border border-border text-muted-foreground hover:bg-accent hover:text-foreground ${TAP}`}>
        <Icon name="more-vertical" size={14} />
      </button>
      {rect && (
        <FloatingPanel anchor={rect} onClose={close} label={t('listings_actions')} className="w-72 p-2 space-y-2">
          <div className="px-2 pt-1 text-xs font-semibold text-foreground truncate">{agentLabel}</div>
          {onToggleLock && !lockAsk && (
            <button type="button" role="menuitem" disabled={busy} onClick={() => (locked ? doLock(null) : setLockAsk(true))}
              className={`${item} ${locked ? TONE.ok.text : TONE.bad.text} disabled:opacity-50`}>
              <Icon name={locked ? 'lock-open' : 'lock'} size={14} />{locked ? t('listings_unlock_action') : t('listings_lock_action')}
            </button>
          )}
          {onToggleLock && lockAsk && (
            <form noValidate className="px-2 py-1 space-y-2" onSubmit={e => { e.preventDefault(); doLock(reason.trim()) }}>
              <div className={`text-xs whitespace-pre-line leading-relaxed ${TONE.bad.text}`}>{tLog('listings_lock_confirm', { e: agentLabel })}</div>
              <input type="text" value={reason} onChange={e => setReason(e.target.value)} placeholder={t('listings_lock_reason')} aria-label={t('listings_lock_reason')}
                className={`w-full rounded-md border border-input px-2 h-8 text-xs bg-background ${TAP}`} />
              <div className="flex gap-2">
                <button type="submit" disabled={busy} className={`${BTN} !border-red-600 !bg-red-600 !text-white disabled:opacity-50`}><Icon name="lock" size={12} />{t('listings_lock_action')}</button>
                <button type="button" onClick={() => { setLockAsk(false); setReason('') }} className={`${BTN} text-muted-foreground`}>{t('listings_paid_cancel')}</button>
              </div>
            </form>
          )}
          {/* TWO SWITCHES (owner 2026-10-29 "two buttons reshare and wa, not three", core 4036): automatic reposts and
              WhatsApp - ok while on, warn while paused. */}
          {switches && [['reshare', 'reshare_off', 'repeat'], ['wa', 'wa_off', 'message-circle']].map(([part, key, icon]) => (
            <button key={part} type="button" role="menuitemcheckbox" aria-checked={!worker[key]}
              onClick={() => onSetWorkerPart(part, !worker[key])} title={t(`my_worker_part_hint_${part}`)} className={item}>
              <Icon name={icon} size={14} />
              <span className="flex-1">{t(`my_worker_part_${part}`)}</span>
              <span className={`${COUNTER} ${TONE[worker[key] ? 'warn' : 'ok'].chip}`}>
                {worker[key] ? t('my_worker_part_off') : t('my_worker_part_on')}
              </span>
            </button>
          ))}
          {/* what the PC is DOING, next to the setting (audit 2026-10-21) - from the server's live browser heartbeats */}
          {switches && (
            <div className="px-2 py-1 border-t border-border/60 flex flex-col gap-1 text-xs">
              <span className={SLOT}><PcSlot pc={worker.pc} full /></span>
              {worker.pc?.auto_update && <span className={SLOT}><UpdaterSlot au={worker.pc.auto_update} full /></span>}
            </div>
          )}
          {paid && <div className="px-2 py-2 border-t border-border/60"><PaidLimit paid={worker.paid} onSet={onPaidOverride} /></div>}
        </FloatingPanel>
      )}
    </>
  )
}

// The IDENTITY zone: who, the severity dot, today's numbers and presence, the shift. It is also the row's expand button.
function Identity({ label, counts, status, checkIn, shifts, rowTier, open, onToggle }) {
  const hm = x => String(x || '').replace(/(\d{1,2}:\d{2}):\d{2}/, '$1')
  const shiftText = (shifts || []).map(s => `${hm(s.start)}–${hm(s.end)}`).join(', ')
  // presence is said only where a schedule put the agent somewhere - "office" is the PLAN, the check-in is the fact
  const showPresence = status === 'office' || status === 'remote'
  const any = counts.published > 0 || counts.failed > 0
  // line 2 is icon + value pieces (review 2026-11-03: the row was 5-6 lines and the rail a thin strip beside it);
  // the words are in the title, and the expanded history says everything
  const inner = (
    <span className="min-w-0 flex-1">
      <span className="block font-medium text-sm truncate leading-snug" title={label}>
        {label}
        {/* 0427 severity dot (designer 1a): one dot after the name, worst tier wins, NO NUMBER - a row with no dot owes nothing */}
        {rowTier && <span className="inline-block align-middle ml-1.5 w-2 h-2 rounded-full" style={{ backgroundColor: NUDGE_FILL[rowTier] }} />}
      </span>
      <span className="flex flex-wrap items-center gap-x-2.5 text-xs text-muted-foreground tabular-nums leading-5">
        {any && (
          <span className="inline-flex items-center gap-1.5" title={tLog('listings_counts_compact', { p: counts.published, f: counts.failed })}>
            <span className={`inline-flex items-center gap-0.5 ${counts.published ? TONE.ok.text : ''}`}><Icon name="check" size={12} />{counts.published}</span>
            {counts.failed > 0 && <span className={`inline-flex items-center gap-0.5 ${TONE.bad.text}`}><Icon name="x" size={12} />{counts.failed}</span>}
          </span>
        )}
        {showPresence && (checkIn
          ? <span className={`inline-flex items-center gap-0.5 ${TONE.ok.text}`} title={tLog('listings_checked_in', { t: checkIn })}><Icon name="user-check" size={12} />{checkIn}</span>
          : <span className="inline-flex items-center gap-0.5 text-muted-foreground/70" title={t('listings_no_checkin')}><Icon name="user-check" size={12} />—</span>)}
        {shiftText && <span className="inline-flex items-center gap-0.5" title={t('listings_legend_shift')}><Icon name="clock" size={12} />{shiftText}</span>}
      </span>
    </span>
  )
  if (!onToggle) return <div className="flex items-start gap-2">{inner}</div>
  return (
    <button type="button" onClick={onToggle} aria-expanded={open} aria-label={`${label} · ${t('listings_details')}`}
            className="flex items-start gap-1.5 text-left w-full rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">
      <span className={`inline-flex text-muted-foreground pt-1 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden="true"><Icon name="chevron-down" size={12} /></span>
      {inner}
    </button>
  )
}

function AgentRow({ rowId, events, status, scale, agentLabel, shifts = [], nowMinute, versionInfo, latestVersion, locked = false, onToggleLock, meetings = [], nudgeTiers = null, balances = null, autoIds = null, worker = null, onSetWorkerPart, onPaidOverride = null, sync = null, checkIn = null, slots = ['version'], hasActions = false, coops = EMPTY }) {
  const [open, setOpen] = useState(false)
  const [shown, setShown] = useState(HISTORY_PAGE)
  const rowTier = (() => {
    if (!nudgeTiers) return null
    let worst = null
    const rank = { medium: 1, high: 2, critical: 3 }
    for (const mt of meetings) {
      const tier = nudgeTiers.get(mt.id)
      if (tier && (!worst || rank[tier] > rank[worst])) worst = tier
    }
    return worst
  })()
  const scrollRef = useScrollToNow(scale, nowMinute, sync)
  const counts = useMemo(() => countOutcomes(events), [events])
  const history = useMemo(() => [...events].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [events])
  const toggle = () => setOpen(o => !o)

  return (
    <div id={rowId ? `activity-row-${rowId}` : undefined} className="border-b border-border/50 last:border-0">
      <Zones className="px-4 py-2"
        identity={<Identity label={agentLabel} counts={counts} status={status} checkIn={checkIn} shifts={shifts} rowTier={rowTier} open={open} onToggle={toggle} />}
        status={<StatusSlots slots={slots} worker={worker} versionInfo={versionInfo} latestVersion={latestVersion} locked={locked} paid={worker?.paid && onPaidOverride ? worker.paid : null} />}
        rail={
          <div ref={scrollRef} className="min-w-0 overflow-x-auto no-scrollbar lg:flex-1 lg:flex lg:flex-col" style={{ scrollbarWidth: 'none' }}>
            {/* meetings alone are enough to draw the rail: an agent who posted nothing but held a viewing did work */}
            {events.length > 0 || shifts.length > 0 || meetings.length > 0 || coops.length > 0
              ? <AgentTimeline events={events} scale={scale} shifts={shifts} nowMinute={nowMinute} meetings={meetings} nudgeTiers={nudgeTiers} autoIds={autoIds} autoLog={worker?.auto_log || null} pcLog={worker?.pc_log || null} pcGaps={todaysGaps(worker?.pc?.gaps, nowMinute)} coops={coops} />
              : <EmptyRail scale={scale} />}
          </div>}
        counts={<Tallies perPlat={counts.perPlat} balances={balances} coops={coops.length} />}
        actions={hasActions ? <RowActionsMenu agentLabel={agentLabel} locked={locked} onToggleLock={onToggleLock}
          worker={worker} onSetWorkerPart={onSetWorkerPart} onPaidOverride={onPaidOverride} /> : null}
      />
      {/* expanded session list — readable outcome + reason, an "Open advert" link instead of a cut URL, paged */}
      {open && events.length > 0 && <SessionHistory history={history} shown={shown} onMore={() => setShown(n => n + HISTORY_PAGE)} autoIds={autoIds} />}
    </div>
  )
}

function SessionHistory({ history, shown, onMore, autoIds }) {
  return (
    <div className="px-4 pb-2">
      {history.slice(0, shown).map(ev => {
        const o = outcomeOf(ev)
        return (
          <div key={ev.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs py-1 border-t border-border/30">
            <span className="text-muted-foreground w-12 shrink-0 tabular-nums">{fmtClock(ev.created_at)}</span>
            <span className={`${COUNTER} font-medium ${PLATFORM_COLOR[ev.platform] || 'bg-muted text-muted-foreground'}`}>
              {PLATFORM_LABEL[ev.platform] || ev.platform}
            </span>
            <span className={`font-medium ${OUTCOME_TONE[o]}`}>{t(OUTCOME_LABEL[o])}</span>
            {isAutoPost(ev, autoIds) && <span style={{ color: AUTO_FILL }} title={t('listings_legend_auto')}><Icon name="bot" size={12} /></span>}
            {ev.deal_type && <span className="text-muted-foreground">{dealLabel(ev.deal_type)}</span>}
            {o === 'failed' && (
              <span className={TONE.bad.text}>{ev.fail_reason ? tLog('listings_fail_reason', { r: ev.fail_reason }) : t('listings_myday_no_reason')}</span>
            )}
            {ev.offer_url && (
              <a href={ev.offer_url} target="_blank" rel="noopener noreferrer" title={ev.offer_url}
                 className="ml-auto inline-flex items-center gap-1 text-primary hover:underline py-1 [@media(pointer:coarse)]:min-h-[44px]">
                {t('listings_open_advert')}<Icon name="external-link" size={12} />
              </a>
            )}
          </div>
        )
      })}
      {history.length > shown && (
        <button type="button" onClick={onMore} className={`text-xs text-primary hover:underline py-2 ${TAP}`}>
          {tLog('listings_show_more', { n: Math.min(HISTORY_PAGE, history.length - shown) })}
        </button>
      )}
    </div>
  )
}

// "MY DAY" (owner 2026-11-03, "Top batch + My day"): an agent opened this tab and found a team page with nothing centred
// on them. Their own row is a card at the top now - today's published and failed with every failure's reason in plain
// text (not a hover), meetings, check-in, then their own rail laid out by the SAME <Zones> as the team rows (with its own
// hour ruler, since the team below may be folded), and the time each balance was read. The team rows sit below, folded.
function MyDayCard({ row, scale, nowMinute, sync, meetings = [], nudgeTiers, autoIds, balances, worker, versionInfo, latestVersion, coops = EMPTY }) {
  const scrollRef = useScrollToNow(scale, nowMinute, sync)
  const counts = useMemo(() => countOutcomes(row.events), [row.events])
  const failures = useMemo(() => row.events.filter(e => outcomeOf(e) === 'failed').sort((a, b) => new Date(b.created_at) - new Date(a.created_at)), [row.events])
  const held = meetings.filter(m => m.status !== 'moved').length
  const scheduled = row.status === 'office' || row.status === 'remote'
  const slots = worker ? ['pc', 'updater', 'version', 'access'] : ['version']
  return (
    <section aria-label={t('listings_myday_title')} className={`${CARD} overflow-hidden`}>
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 px-4 py-2 border-b border-border/60">
        <h3 className="text-sm font-semibold">{t('listings_myday_title')}</h3>
        <span className="text-sm text-muted-foreground break-words">{row.label}</span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 px-4 py-3">
        <StatTile label={t('listings_status_published')} value={counts.published} tone={counts.published ? 'ok' : null}
          sub={counts.pending || counts.filled ? tLog('listings_kpi_fail_sub', { p: counts.pending, f: counts.filled }) : null} />
        <StatTile label={t('listings_status_failed')} value={counts.failed} tone={counts.failed ? 'bad' : null} />
        <StatTile label={t('listings_kpi_meetings')} value={held} />
        <StatTile label={t('listings_myday_checkin')} value={row.checkIn || '—'} tone={row.checkIn ? 'ok' : null}
          sub={!scheduled ? t('listings_myday_off') : null} />
      </div>
      <Zones className="px-4 py-2 border-t border-border/60"
        identity={<Identity label={t('listings_myday_rail')} counts={{ published: 0, failed: 0 }} status={row.status} checkIn={row.checkIn} shifts={row.shifts} rowTier={null} />}
        status={<StatusSlots slots={slots} worker={worker} versionInfo={versionInfo} latestVersion={latestVersion} />}
        rail={
          <div ref={scrollRef} className="min-w-0 overflow-x-auto no-scrollbar" style={{ scrollbarWidth: 'none' }}>
            <div style={{ width: scale.totalW }}>
              <TimelineAxis scale={scale} nowMinute={nowMinute} />
              {row.events.length > 0 || (row.shifts || []).length > 0 || meetings.length > 0 || coops.length > 0
                ? <AgentTimeline events={row.events} scale={scale} shifts={row.shifts || EMPTY} nowMinute={nowMinute} meetings={meetings} nudgeTiers={nudgeTiers} autoIds={autoIds} autoLog={worker?.auto_log || null} pcLog={worker?.pc_log || null} pcGaps={todaysGaps(worker?.pc?.gaps, nowMinute)} coops={coops} />
                : <EmptyRail scale={scale} />}
            </div>
          </div>}
        counts={<Tallies perPlat={counts.perPlat} balances={balances} coops={coops.length} />}
      />
      {failures.length > 0 && (
        <div className="px-4 py-2 border-t border-border/60">
          <div className={`text-xs font-semibold ${TONE.bad.text}`}>{t('listings_myday_failures')} ({failures.length})</div>
          <ul className="divide-y divide-border/40">
            {failures.map(ev => (
              <li key={ev.id} className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs py-1">
                <span className="text-muted-foreground w-12 shrink-0 tabular-nums">{fmtClock(ev.created_at)}</span>
                <span className={`${COUNTER} font-medium ${PLATFORM_COLOR[ev.platform] || 'bg-muted text-muted-foreground'}`}>{PLATFORM_LABEL[ev.platform] || ev.platform}</span>
                {ev.deal_type && <span className="text-muted-foreground">{dealLabel(ev.deal_type)}</span>}
                <span className={`${TONE.bad.text} break-words min-w-0`}>{ev.fail_reason || t('listings_myday_no_reason')}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  )
}

// ─── monthly daily-counts chart ───────────────────────────────────────────────

// One bar per day of the month = published-ad count that day. Counts come from
// per-day HEAD count queries (no row transfer) — a busy month is ~26k event rows,
// so paging them down just to count would be far heavier than 31 tiny requests.
// Count published ads on one calendar day. HEAD count — no row transfer.
// The window is the GEORGIAN day (owner ruling 2026-08-12), never the device's.
async function countPublishedOn(dateStr) {
  const { startIso, endIso } = tbilisiDayWindow(dateStr)
  const { count, error } = await testDb
    .from('user_events')
    .select('id', { count: 'exact', head: true })
    .eq('published', true)
    .gte('created_at', startIso)
    .lte('created_at', endIso)
  if (error) throw error
  return count || 0
}

// Published counts for a list of Tbilisi dates: ONE grouped read (rpc user_events_published_daily, server/sql/, applied
// 2026-10-21) instead of one HEAD count per day - 31 requests, 62 with the comparison. Future dates stay null.
// ⚠️ If the function is missing or refuses, it falls back to the per-day counts rather than drawing an empty month.
async function countPublishedDays(dates, todayStr) {
  const past = dates.filter(d => d <= todayStr)
  if (!past.length) return dates.map(date => ({ date, count: null }))
  const from = past.reduce((a, b) => (a < b ? a : b)), to = past.reduce((a, b) => (a > b ? a : b))
  const { data, error } = await testDb.rpc('user_events_published_daily', { p_from: from, p_to: to })
  if (!error && Array.isArray(data)) {
    const byDay = new Map(data.map(r => [String(r.day).slice(0, 10), Number(r.n) || 0]))
    return dates.map(date => ({ date, count: date > todayStr ? null : (byDay.get(date) || 0) }))
  }
  console.warn('[listings-month] grouped count unavailable, counting day by day:', error?.message)
  return Promise.all(dates.map(async date => ({ date, count: date > todayStr ? null : await countPublishedOn(date) })))
}

// ── the comparison overlay is aligned by WEEKDAY, never by date ───────────────
// Posting volume follows the WEEK (weekends are structurally quiet), so laying the
// previous month's 1st over this month's 1st compares a Tuesday against a Saturday
// and shifts the whole shape by a meaningless offset.
// Anchor = the date NEAREST the previous month's 1st that falls on the same weekday
// as THIS month's 1st. The first such match inside the previous month is always its
// day 1–7; when it sits on day 5–7 the match one week EARLIER is closer, and that one
// lands on the 29th/30th/31st of the month before it — which is then the anchor.
// From the anchor the overlay runs day by day, so every column pairs like weekday
// with like weekday for the whole month.
function overlayAnchorDate(month) {
  const [y, m] = month.split('-').map(Number)
  const wantWd      = new Date(y, m - 1, 1).getDay()  // this month's 1st
  const prevFirstWd = new Date(y, m - 2, 1).getDay()  // previous month's 1st
  const k = 1 + ((wantWd - prevFirstWd + 7) % 7)      // 1..7 — first match in prev month
  // forward distance k-1 vs backward distance 8-k → from k >= 5 the earlier week wins.
  return new Date(y, m - 2, k <= 4 ? k : k - 7)       // day <= 0 rolls into prev-prev month
}

export function MonthlyAdsChart({ selectedDate, onPickDay, scopeNote = null }) {
  const [month, setMonth] = useState(() => tbilisiToday().slice(0, 7)) // 'YYYY-MM'
  const [counts, setCounts] = useState(null) // [{ date, day, future, count|null }] — null count = future day
  const [error, setError] = useState(null)
  const [overlay, setOverlay] = useState(false)
  const [prev, setPrev] = useState(null)     // { month, rows:[{date,count}] } — weekday-aligned
  const prevReqRef = useRef(null)            // month whose overlay is fetched / in flight
  // ⚠️ On a phone the plot scrolls sideways and used to open on the 1st, with today off-screen (owner 2026-10-22:
  // "it must center the today by default"). Once the month's bars are in, the selected day - or today - is scrolled to
  // the middle; again when the month, the comparison width or the selected day changes. Never fights a manual scroll
  // in between, since nothing else moves it.
  const plotScrollRef = useRef(null)

  const todayStr = tbilisiToday() // Georgian today, not the device's
  const thisMonth = todayStr.slice(0, 7)

  useEffect(() => {
    if (!testDb) return
    let cancelled = false
    setCounts(null); setError(null)
    const [y, m] = month.split('-').map(Number)
    const daysInMonth = new Date(y, m, 0).getDate()
    // The month is drawn IN FULL from the 1st: a day that has not happened yet keeps
    // its (empty) column, so the shape of the month is stable all month long instead of
    // growing a column a day. Future days are never queried — they carry count null.
    const days = []
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${month}-${String(d).padStart(2, '0')}`
      days.push({ date: dateStr, day: d, future: dateStr > todayStr })
    }
    ;(async () => {
      const got = await countPublishedDays(days.map(r => r.date), todayStr)
        .catch(e => { if (!cancelled) setError(e.message); return null })
      if (!cancelled && got) setCounts(days.map((row, i) => ({ ...row, count: row.future ? null : got[i].count })))
    })()
    return () => { cancelled = true }
  }, [month, todayStr])

  // Overlay counts are fetched once per month on first reveal (one more HEAD query per
  // day) and kept — toggling twice must not re-run them. The in-flight month lives in a
  // ref rather than in state, so the fetch is never cancelled by its own bookkeeping.
  useEffect(() => {
    if (!testDb || !overlay) return
    if (prevReqRef.current === month) return
    prevReqRef.current = month
    let cancelled = false
    const [y, m] = month.split('-').map(Number)
    const daysInMonth = new Date(y, m, 0).getDate()
    const anchor = overlayAnchorDate(month)
    const dates = []
    for (let i = 0; i < daysInMonth; i++) {
      dates.push(toDateStr(new Date(anchor.getFullYear(), anchor.getMonth(), anchor.getDate() + i)))
    }
    let done = false
    ;(async () => {
      const rows = await countPublishedDays(dates, todayStr)
        .catch(() => { prevReqRef.current = null; return null })
      done = true
      if (!cancelled && rows) setPrev({ month, rows })
    })()
    // ⚠️ hidden again before the fetch landed → its result is dropped, so the month must NOT stay marked as fetched,
    // or the next reveal finds the ref set and waits forever on "…" (audit 2026-10-21)
    return () => { cancelled = true; if (!done && prevReqRef.current === month) prevReqRef.current = null }
  }, [overlay, month, todayStr])

  function shiftMonth(delta) {
    const [y, m] = month.split('-').map(Number)
    const d = new Date(y, m - 1 + delta, 1)
    const next = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    if (next > thisMonth) return
    setMonth(next)
  }

  const prevRows = overlay && prev?.month === month ? prev.rows : null
  const max = Math.max(
    1,
    ...(counts?.map(c => c.count || 0) ?? []),
    ...(prevRows?.map(r => r.count || 0) ?? []),
  )
  const total = counts?.reduce((s, c) => s + (c.count || 0), 0) ?? 0
  // Compare like with like: the previous period's total counts only the columns this
  // month has actually reached, or a part-month always "loses" against a whole one.
  const prevSoFar = prevRows
    ? prevRows.reduce((s, r, i) => s + (counts?.[i]?.count == null ? 0 : (r.count || 0)), 0)
    : 0
  // Round the axis top up to a friendly step so gridline labels aren't ragged.
  const step = max <= 20 ? 5 : max <= 100 ? 25 : max <= 500 ? 100 : 250
  const axisMax = Math.max(step, Math.ceil(max / step) * step)
  const gridLines = [1, 2, 3].map(i => Math.round((axisMax * i) / 4)).concat(axisMax)

  const lang = getLanguage()
  // the UI language, not the browser's (a Georgian Desk read "October 2026")
  const uiLocale = lang === 'ka' ? 'ka-GE' : 'en-GB'
  const monthLabel = new Date(`${month}-01T12:00:00`).toLocaleDateString(uiLocale, { month: 'long', year: 'numeric' })
  const CHART_H = 160

  // Localized short weekday names (Sun..Sat). Georgian hardcoded — the browser's
  // ka-GE short form is unreliable across platforms; en falls back to Intl.
  const WEEKDAYS_KA = ['კვ', 'ორშ', 'სამ', 'ოთხ', 'ხუთ', 'პარ', 'შაბ']
  const weekdayShort = (dateStr) => {
    const d = new Date(`${dateStr}T12:00:00`)
    if (lang === 'ka') return WEEKDAYS_KA[d.getDay()]
    return d.toLocaleDateString('en', { weekday: 'short' })
  }
  const isWeekend = (dateStr) => {
    const wd = new Date(`${dateStr}T12:00:00`).getDay()
    return wd === 0 || wd === 6
  }
  const shortDate = (dateStr) =>
    new Date(`${dateStr}T12:00:00`).toLocaleDateString(uiLocale, { day: 'numeric', month: 'short' })

  const colW = overlay ? 34 : 26
  const focusDay = counts?.some(c => c.date === selectedDate) ? selectedDate : todayStr
  useEffect(() => {
    const el = plotScrollRef.current
    if (!el || !counts || el.scrollWidth <= el.clientWidth) return
    const i = counts.findIndex(c => c.date === focusDay)
    if (i < 0) return
    const col = el.scrollWidth / counts.length
    el.scrollLeft = Math.max(0, (i + 0.5) * col - el.clientWidth / 2)
  }, [counts, colW, focusDay])
  const deltaPct = prevSoFar > 0 ? Math.round(((total - prevSoFar) / prevSoFar) * 100) : null

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden">
      <div className="flex flex-wrap items-center gap-3 px-4 py-2.5 border-b border-border/60">
        <div>
          <div className="text-sm font-semibold">{t('listings_month_chart_title')}</div>
          <div className="text-xs text-muted-foreground">{t('listings_month_chart_sub')}{scopeNote && <span className="text-amber-600 dark:text-amber-500"> · {scopeNote}</span>}</div>
        </div>
        <div className="ml-auto flex flex-wrap items-center gap-1.5">
          {counts && (
            <span className="text-xs text-muted-foreground mr-1 tabular-nums">
              <span className="font-semibold text-foreground">{total}</span> {t('listings_month_total')}
              {prevRows && (
                <span className="ml-2 text-amber-600 dark:text-amber-500">
                  <span className="font-semibold">{prevSoFar}</span> {t('listings_overlay_total')}
                  {deltaPct != null && (
                    <span className="ml-1 opacity-80">({deltaPct >= 0 ? '+' : '−'}{Math.abs(deltaPct)}%)</span>
                  )}
                </span>
              )}
            </span>
          )}
          <button onClick={() => setOverlay(v => !v)}
            title={overlay ? t('listings_overlay_hide') : t('listings_overlay_show')}
            className={`text-xs px-2 h-7 flex items-center gap-1.5 rounded-lg border transition-colors ${
              overlay
                ? 'border-amber-500/60 bg-amber-500/10 text-amber-600 dark:text-amber-400'
                : 'border-border hover:bg-accent text-muted-foreground'
            }`}>
            <span className="w-2.5 h-2.5 rounded-[3px] border border-amber-500/80 bg-amber-500/30" />
            {overlay ? t('listings_overlay_hide') : t('listings_overlay_show')}
          </button>
          {/* ‹ month › is ONE unit (owner 2026-10-22, iPhone: "month switcher must be on the line"): as three loose
              items of the wrapping row, ‹ stayed beside the compare button and "October, 2026 ›" fell to the next line */}
          <div className="flex shrink-0 items-center gap-1.5">
            <button onClick={() => shiftMonth(-1)} title={t('listings_prev_month')} aria-label={t('listings_prev_month')}
              className="shrink-0 text-sm w-9 h-9 flex items-center justify-center rounded-lg border border-border hover:bg-accent">‹</button>
            <span className="text-xs font-medium w-32 text-center">{monthLabel}</span>
            <button onClick={() => shiftMonth(1)} disabled={month >= thisMonth} title={t('listings_next_month')} aria-label={t('listings_next_month')}
              className="shrink-0 text-sm w-9 h-9 flex items-center justify-center rounded-lg border border-border hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed">›</button>
          </div>
        </div>
      </div>
      <div className="px-4 pt-3 pb-2">
        {error ? (
          <div className="py-10 text-center text-red-500 text-sm">{error}</div>
        ) : !counts ? (
          <div className="text-center text-muted-foreground text-sm" style={{ lineHeight: `${CHART_H}px` }}>…</div>
        ) : (
          <div className="flex gap-2">
            {/* y axis labels */}
            <div className="relative shrink-0 w-8 text-[9px] text-muted-foreground tabular-nums" style={{ height: CHART_H }}>
              {gridLines.map(v => (
                <span key={v} className="absolute right-0 -translate-y-1/2" style={{ top: CHART_H - (v / axisMax) * CHART_H }}>{v}</span>
              ))}
              <span className="absolute right-0 bottom-0 translate-y-1/2">0</span>
            </div>
            {/* Plot scrolls horizontally when bars would squish below a readable width
                (mobile) — each column keeps ≥ 26px, ≥ 34px once it holds a pair; the
                y-axis stays pinned left. */}
            <div ref={plotScrollRef} className="flex-1 min-w-0 overflow-x-auto">
              <div style={{ minWidth: counts.length * colW }}>
                {/* plot area */}
                <div className="relative border-b border-border/60" style={{ height: CHART_H }}>
                  {gridLines.map(v => (
                    <div key={v} className="absolute left-0 right-0 h-px bg-border/25" style={{ top: CHART_H - (v / axisMax) * CHART_H }} />
                  ))}
                  <div className="absolute inset-0 flex items-end gap-[2px]">
                    {counts.map((c, i) => {
                      const h = c.count == null ? 0 : Math.round((c.count / axisMax) * CHART_H)
                      const p = prevRows?.[i]
                      const ph = p?.count == null ? 0 : Math.round((p.count / axisMax) * CHART_H)
                      const isSelected = c.date === selectedDate
                      // a real button per day (audit 2026-10-21: clickable divs had no keyboard access or name)
                      return (
                        <button type="button" key={c.date} disabled={c.future}
                             onClick={() => onPickDay?.(c.date)}
                             aria-label={`${shortDate(c.date)} (${weekdayShort(c.date)}) · ${c.count ?? '—'}`}
                             aria-pressed={isSelected}
                             className={`flex-1 min-w-0 h-full flex items-end justify-center gap-[2px] group rounded-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-primary ${
                               c.future ? 'cursor-default' : 'cursor-pointer'
                             }`}>
                          <div title={`${c.date} · ${c.count ?? '—'}`}
                               className={`${prevRows ? 'w-1/2' : 'w-full'} rounded-t-[4px] transition-colors ${
                                 isSelected
                                   ? 'bg-emerald-500'
                                   : 'bg-emerald-600/55 dark:bg-emerald-500/45 group-hover:bg-emerald-500'
                               }`} style={{ height: Math.max(c.count > 0 ? 2 : 0, h) }} />
                          {prevRows && (
                            <div title={p ? `${shortDate(p.date)} (${weekdayShort(p.date)}) · ${p.count ?? '—'}` : ''}
                                 className="w-1/2 rounded-t-[4px] border-t-2 border-x border-amber-500/70 bg-amber-500/15 dark:bg-amber-500/10"
                                 style={{ height: Math.max((p?.count || 0) > 0 ? 2 : 0, ph) }} />
                          )}
                        </button>
                      )
                    })}
                  </div>
                </div>
                {/* x axis: day number + short weekday (weekends tinted amber) */}
                <div className="flex gap-[2px] pt-1">
                  {counts.map(c => {
                    const sel = c.date === selectedDate
                    const wknd = isWeekend(c.date)
                    return (
                      <div key={c.date} onClick={() => { if (!c.future) onPickDay?.(c.date) }} aria-hidden="true"
                           className={`flex-1 min-w-0 text-center text-xs tabular-nums leading-tight ${
                             c.future ? 'cursor-default opacity-45' : 'cursor-pointer'
                           } ${
                             sel ? 'text-emerald-500 font-semibold' : wknd ? 'text-amber-600/80 dark:text-amber-500/80' : 'text-muted-foreground'
                           }`}>
                        <div>{c.day}</div>
                        <div className="text-xs opacity-80 truncate">{weekdayShort(c.date)}</div>
                      </div>
                    )
                  })}
                </div>
              </div>
            </div>
          </div>
        )}
        {overlay && counts && (
          <div className="flex flex-wrap items-center gap-x-2 gap-y-1 pt-2 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1">
              <span className="w-2.5 h-2.5 rounded-[3px] border-t-2 border-x border-amber-500/70 bg-amber-500/15" />
              {t('listings_overlay_legend')}
            </span>
            {prevRows
              ? <span className="tabular-nums">· {shortDate(prevRows[0].date)} – {shortDate(prevRows[prevRows.length - 1].date)} · {t('listings_overlay_note')}</span>
              : <span>· …</span>}
          </div>
        )}
      </div>
    </div>
  )
}
// ─── main ──────────────────────────────────────────────────────────────────────

// ⚠️ Every user_events column the tab reads (checked against the Desk DB 2026-10-21). A new field shown on a dot or in the
// history must be added here, or it is silently undefined.
const DAY_EVENT_COLUMNS = 'id,session_id,user_id,user_email,platform,deal_type,completed,published,failed,fail_reason,offer_url,created_at,post_count,group_count'

// Today's events are re-read this often while the tab is visible (audit 2026-10-21: the now line moved while the
// numbers stood still - a remount showed 2,248 published where the open page still said 2,104).
const LIVE_REFRESH_MS = 2 * 60_000
// past this age a today view says it is behind, even if no read failed (sleeping laptop, throttled background tab)
const STALE_AFTER_MS = 3 * 60_000

// Mount the monthly chart only when it scrolls near the screen: it sits under every agent row and fires one count
// query per day of the month (two months with the comparison) the moment it mounts.
function LazyMount({ children, minHeight = 220 }) {
  const ref = useRef(null)
  const [seen, setSeen] = useState(() => typeof IntersectionObserver === 'undefined')
  useEffect(() => {
    if (seen || !ref.current) return
    const io = new IntersectionObserver(es => { if (es.some(e => e.isIntersecting)) setSeen(true) }, { rootMargin: '400px' })
    io.observe(ref.current)
    return () => io.disconnect()
  }, [seen])
  return <div ref={ref} style={seen ? undefined : { minHeight }}>{seen ? children : null}</div>
}

// `role` gates ONLY the 🔒 lock chip (agent_license kill-switch). agent / agent_hr get this
// tab read-only (2026-07-22) — everything else here is already read-only.
// `agents` = the roster AFTER the office filter, `allAgents` = the whole agency. Both are needed: events are read
// agency-wide, and only the whole roster can tell "another office's agent" from "a login nobody knows".
// `myAgentId` = the viewer's own roster agent (App's myAgentRecord) - the "My day" card is theirs.
export function ListingsTab({ agents: agentsProp = [], allAgents: allAgentsProp = null, officeFilterActive = false, role = null, myAgentId = null }) {
  const headerH = useAppHeaderHeight()
  const canLock = can(role, 'listings.lock')
  // an agent / agent_hr reads this tab for THEIR day: "My day" on top, the team folded underneath (owner 2026-11-03)
  const agentView = role === 'agent' || role === 'agent_hr'
  const [teamOpen, setTeamOpen] = useState(!agentView)
  // the one tap-to-reveal popover (see TipContext)
  const [tip, setTip] = useState(null)
  const tipN = useRef(0)
  const showTip = useCallback((text, anchor, link = null) => {
    const rect = anchor && anchor.getBoundingClientRect ? anchor.getBoundingClientRect()
      : { left: anchor.x, right: anchor.x, top: anchor.y, bottom: anchor.y, width: 0, height: 0 }
    setTip({ text, rect, link, n: ++tipN.current })
  }, [])
  const closeTip = useCallback(() => setTip(null), [])
  // ── Roster fallback (2026-08-01) ────────────────────────────────────────────
  // On an agent's session the SPA's own `agents` read comes back empty, and this tab
  // needs the roster for FOUR things at once: the row label (name, not a raw login), the
  // attendance grouping, the shift bands, and the meeting markers — which key on
  // agents.id, so with no roster they land on rows that do not exist. The live RLS policy
  // is wide open (authenticated_all / ALL / true, checked 2026-08-01) so the direct read
  // SHOULD work; rather than ship another theory, ask the server, which holds the
  // scheduler service key and cannot be denied.
  //
  // Fallback ONLY — it fires when the prop is empty and never overrides a working roster,
  // so if the direct read is healthy this endpoint is never called. Identity fields only
  // (lib/sched_roster.js): no phones, no salaries, no positions.
  const [rosterFallback, setRosterFallback] = useState([])
  useEffect(() => {
    if (agentsProp.length) { setRosterFallback([]); return }
    let gone = false
    getSchedulerRoster()
      .then(rows => { if (!gone) setRosterFallback(rows || []) })
      .catch(e => console.error('[listings-roster]', e.message))
    return () => { gone = true }
  }, [agentsProp.length])
  // ⚠️ Rows from the fallback carry NO `schedule`, so scheduledSlotsOnDate/scheduledLocation
  // find nothing and everyone lands under "Off" with no shift band. That is a real and
  // deliberate degradation — a name and a working meeting marker beat a correct section
  // header — but do not read the grouping as truth when this path is active.
  const agents = agentsProp.length ? agentsProp : rosterFallback
  const allAgents = allAgentsProp && allAgentsProp.length ? allAgentsProp : agents
  const rosterFromServer = !agentsProp.length && rosterFallback.length > 0
  const [date,    setDate]    = useState(tbilisiToday())
  // ⚠️ The day's events are stored WITH the date they belong to (audit 2026-10-21). An older day's request that
  // finished after a newer one used to overwrite it, and a failed read left the previous day's array in place, so
  // the headline could show another day's numbers under the selected date. Anything not tagged with `date` is not
  // shown for it.
  const [day,     setDay]     = useState({ date: null, events: [], fetchedAt: null })
  const [error,   setError]   = useState(null)
  const [reloadTick, setReloadTick] = useState(0)
  const reqGen = useRef(0)
  const isToday = date === tbilisiToday()

  // Attendance + schedule + overrides for the selected month (for status ordering)
  const monthStr = date.slice(0, 7)
  const { from: mFrom, to: mTo } = monthBounds(date)
  const { byAgent: attByAgent, scheduleHistory } = useAttendance(monthStr, agents)
  const { byAgent: overrideMap } = useOverrides(agents, mFrom, mTo)

  // Fetch events for the Tbilisi day - on a date change, on Refresh, and every 2 minutes while today is on screen
  useEffect(() => {
    if (!testDb) { setError('Supabase not configured'); return }
    const gen = ++reqGen.current
    const forDate = date
    const { startIso: dayStartIso, endIso: dayEndIso } = tbilisiDayWindow(forDate)
    // PostgREST caps responses at 1000 rows; a busy day can exceed that, so page
    // through with .range() rather than silently dropping the tail of the day.
    ;(async () => {
      const PAGE = 1000
      const all = []
      try {
        for (let offset = 0; ; offset += PAGE) {
          const { data, error: e } = await testDb
            .from('user_events')
            // only what the tab reads - not corrections (jsonb), browser/os, durations (audit 2026-10-21)
            .select(DAY_EVENT_COLUMNS)
            .gte('created_at', dayStartIso)
            .lte('created_at', dayEndIso)
            .order('created_at', { ascending: true })
            .order('id', { ascending: true })
            .range(offset, offset + PAGE - 1)
          if (gen !== reqGen.current) return          // a newer read owns the screen
          if (e) throw e
          if (!data?.length) break
          all.push(...data)
          if (data.length < PAGE) break
        }
        setDay({ date: forDate, events: all, fetchedAt: new Date() })
        setError(null)
      } catch (e) {
        if (gen !== reqGen.current) return
        // a failed REFRESH keeps the same day's last good read (with a visible "behind" note); a failed first read
        // of a day shows nothing for it - never the previous day's numbers
        setDay(d => d.date === forDate ? d : { date: forDate, events: [], fetchedAt: null })
        setError(e?.message || String(e))
      }
    })()
  }, [date, reloadTick])

  // live refresh while today is open and the page is visible; coming back to a hidden tab refreshes at once
  useEffect(() => {
    if (!isToday) return
    const bump = () => { if (document.visibilityState === 'visible') setReloadTick(n => n + 1) }
    const id = setInterval(bump, LIVE_REFRESH_MS)
    document.addEventListener('visibilitychange', bump)
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', bump) }
  }, [isToday])

  const loaded = day.date === date
  const events = loaded ? day.events : EMPTY

  // ── Meeting markers for the viewed day (owner 2026-08-01) ───────────────────
  // Through the MARKER feed, not the meetings feed. Two reasons, and the second is
  // the load-bearing one:
  //   • the meetings feed filters by created_at, and a viewing held today was very
  //     likely booked last week — the rail needs the SCHEDULED instant;
  //   • the meetings feed is viewer-scoped, so a plain agent saw only their own
  //     markers. Owner ruling: EVERY agent sees everyone's markers here, the same
  //     way they already see everyone's posting pins. That is only safe because the
  //     marker payload says when / how it ended / whose — and nothing else. The
  //     tooltip therefore carries no client and no address FOR ANYONE, including
  //     admins; detail lives on the Desk, behind the scoped door.
  // cooperations opened this Georgian day (core GET /cooperations/opened, owner 2026-11-03) - diamonds on the rails. A
  // failed read leaves the rails without diamonds; it never blanks the tab.
  // ⚠️ Kept WITH its date (audit 2026-11-03): after a date change the previous day's rows were drawn on the new day at
  // their minute-of-day, widened its bins and set its tile until the new read came back. Rows of another day are ignored.
  const [coopsOpened, setCoopsOpened] = useState({ date: null, rows: [] })
  useEffect(() => {
    let gone = false
    const win = tbilisiDayWindow(date)
    getCooperationsOpened({ from: win.startIso, to: win.endIso })
      .then(rows => { if (!gone) setCoopsOpened({ date, rows: rows || [] }) })
      .catch(e => { if (!gone) { console.error('[listings-coops]', e.message); setCoopsOpened({ date, rows: [] }) } })
    return () => { gone = true }
  }, [date, reloadTick])
  const coopsByAgent = useMemo(() => {
    const m = new Map()
    if (coopsOpened.date !== date) return m
    for (const c of coopsOpened.rows) {
      if (!c.agent_id) continue
      if (!m.has(c.agent_id)) m.set(c.agent_id, [])
      m.get(c.agent_id).push({ ...c, minute: tbilisiMinuteOfDay(c.at) })
    }
    return m
  }, [coopsOpened, date])
  const [meetings, setMeetings] = useState([])
  const [meetingsDown, setMeetingsDown] = useState(false)
  useEffect(() => {
    let gone = false
    const win = tbilisiDayWindow(date) // Georgian day, not the device's
    getMeetingMarkers({ from: win.startIso, to: win.endIso })
      .then(rows => { if (!gone) { setMeetings(rows || []); setMeetingsDown(false) } })
      // A core outage must degrade the rail to "no meetings", never blank the tab - but the KPI says
      // "unavailable", not 0, or an outage reads as a day with nothing to follow up (audit 2026-10-21)
      .catch(e => { if (!gone) { console.error('[listings-meetings]', e.message); setMeetings([]); setMeetingsDown(true) } })
    return () => { gone = true }
  }, [date, reloadTick])

  // ── Nudge severity for the rail (0427, designer variant 1a, 2026-08-06) ────
  // meeting id → worst tier. The dot after an agent's name and the ring on the
  // offending glyph both read from this one map, so "which agent" and "which
  // hour" are answered by the same fact. Viewer-scoped like everything nudge:
  // an agent sees marks on their own meetings only; management sees all.
  // Absence is the message too — a row with no dot owes nothing, so the map is
  // never seeded with anything but the endpoint's own answer.
  const [nudgeTiers, setNudgeTiers] = useState(null)
  const [nudgesDown, setNudgesDown] = useState(false)
  useEffect(() => {
    let gone = false
    getMeetingNudges().then(d => {
      if (gone) return
      const m = new Map()
      for (const tier of ['medium', 'high', 'critical'])   // later write wins → critical
        for (const n of (d?.[tier] || [])) m.set(n.id, tier)
      setNudgeTiers(m)
      setNudgesDown(false)
    }).catch(() => { if (!gone) setNudgesDown(true) })   // degrades to "no marks", never a blank rail - and says so
    return () => { gone = true }
  }, [date])

  // ids of the agents on screen (after the office filter) - meetings and totals are counted for THEM only
  const visibleIds = useMemo(() => new Set(agents.filter(a => employedOn(a, date)).map(a => a.id)), [agents, date])

  // ⚠️ Meeting day membership and rail position are TBILISI wall clock, like the pins (audit 2026-10-21): this used
  // the device's zone, so on a UTC laptop a 01:00 Tbilisi viewing fell out of the day and a 16:00 one was drawn at
  // 12:00 under a 16:00 tooltip.
  const meetingsOnDay = useMemo(
    () => (meetings || []).filter(v => v.at && tbilisiParts(v.at).date === date),
    [meetings, date])

  // Day totals for the meetings KPI. Counted off the MARKER list, not meetingsByAgent —
  // that one fans a joint viewing out to every agent on it, so summing it would report one
  // meeting as two. A ghost ('moved') is not a meeting that happened here: it is reported
  // on its own, after the outcomes, so the headline never inflates.
  // With an office selected only meetings with one of ITS agents count.
  const meetingTotals = useMemo(() => {
    const onDay = officeFilterActive
      ? meetingsOnDay.filter(v => (v.agent_ids || []).some(id => visibleIds.has(id)))
      : meetingsOnDay
    const by = s => onDay.filter(v => v.status === s).length
    const moved = by('moved')
    const parts = [
      by('closed') ? tLog('listings_kpi_meetings_closed', { n: by('closed') }) : null,
      by('cancelled') ? tLog('listings_kpi_meetings_cancelled', { n: by('cancelled') }) : null,
      by('failed') ? tLog('listings_kpi_meetings_failed', { n: by('failed') }) : null,
      moved ? tLog('listings_kpi_meetings_moved', { n: moved }) : null,
    ].filter(Boolean)
    return { held: onDay.length - moved, sub: parts.join(' · ') || null }
  }, [meetingsOnDay, officeFilterActive, visibleIds])

  // agent id → markers. A meeting hangs off every agent on it, so a joint viewing
  // shows on both rails.
  const meetingsByAgent = useMemo(() => {
    const m = new Map()
    const when = iso => { const p = tbilisiParts(iso); return `${p.date} ${p.time}` }
    for (const v of meetingsOnDay) {
      // 'moved' has no entry in MEETING_STATUS_KEYS by design — it is not one of the stored
      // statuses, it is "an appointment stood here and was moved". The tooltip answers the
      // next question ("then when?") from moved_to, so the rail never leaves a ghost the
      // reader cannot explain. Still no client and no address, for anyone.
      const stLabel = v.status === 'moved'
        ? (v.moved_to
            ? tLog('listings_meeting_moved_to', { when: when(v.moved_to) })
            : t('listings_meeting_moved'))
        : v.status === 'revisited'
          ? (v.revisit_at
              ? tLog('listings_meeting_revisited_at', { when: when(v.revisit_at) })
              : t('listings_meeting_revisited'))
        : MEETING_STATUS_KEYS[v.status] ? t(MEETING_STATUS_KEYS[v.status]) : (v.status || '')
      const marker = {
        id: v.id,
        minute: tbilisiMinuteOfDay(v.at),
        status: v.status,
        // The latest comment, on its own line (owner 2026-08-04: "on hover let it show
        // comment"). ⚠️ `note` ARRIVES ONLY FOR MEETINGS THIS VIEWER IS ON — the marker feed
        // is agency-wide and omits the key for everyone else (see core_meeting.js). So a
        // missing note means "not yours to read" as often as "nothing written", and this
        // must never be turned into a placeholder that claims the latter.
        title: `${fmtClock(v.at)} · ${stLabel}${v.note ? '\n' + v.note : ''}`,
      }
      for (const aid of (v.agent_ids || [])) {
        if (!m.has(aid)) m.set(aid, [])
        m.get(aid).push(marker)
      }
    }
    for (const list of m.values()) list.sort((a, b) => a.minute - b.minute)
    return m
  }, [meetingsOnDay])

  // App-version heartbeats (current state — not tied to the viewed date)
  const [appRows, setAppRows] = useState([])
  useEffect(() => {
    if (!testDb) return
    testDb.from('agent_app').select('*').then(({ data }) => setAppRows(data || []))
  }, [])
  // the viewed day's automatic post ids (owner 2026-10-19: ringed dots), refreshed every 5 minutes
  const [autoIds, setAutoIds] = useState(null)
  useEffect(() => {
    let live = true
    const win = tbilisiDayWindow(date)
    const load = () => getAutoPostIds(win.startIso, win.endIso)
      .then(d => { if (live) setAutoIds(new Set((d && d.ids) || [])) }).catch(() => {})
    load()
    const t = setInterval(load, 5 * 60 * 1000)
    return () => { live = false; clearInterval(t) }
  }, [date])
  // each agent's last ss / myhome balance (owner 2026-10-19), refreshed every 5 minutes
  const [balancesByEmail, setBalancesByEmail] = useState({})
  const [walletByPortal, setWalletByPortal] = useState({})
  useEffect(() => {
    let live = true
    const load = () => getPortalBalances().then(d => {
      if (!live) return
      const m = {}
      for (const b of (d && d.balances) || []) (m[String(b.email).toLowerCase()] ||= {})[b.platform] = { gel: b.gel, at: b.at }
      setBalancesByEmail(m)
      setWalletByPortal(Object.fromEntries(((d && d.wallet) || []).map(w => [w.platform, w])))
    }).catch(() => {})
    load()
    const t = setInterval(load, 5 * 60 * 1000)
    return () => { live = false; clearInterval(t) }
  }, [])
  const appByEmail = useMemo(() => {
    const m = {}
    for (const r of appRows) if (r.user_email) m[r.user_email.toLowerCase()] = r
    return m
  }, [appRows])

  // Remote kill-switch state (agent_license). A row with active=false = locked out;
  // no row = allowed. Read by api.sentavus.com to block login/refresh/heartbeat.
  const [licRows, setLicRows] = useState([])
  useEffect(() => {
    if (!testDb) return
    testDb.from('agent_license').select('*').then(({ data }) => setLicRows(data || []))
  }, [])
  const lockedByEmail = useMemo(() => {
    const m = {}
    for (const r of licRows) if (r.email && r.active === false) m[r.email.toLowerCase()] = r
    return m
  }, [licRows])

  // the ⋯ menu has already asked (consequences + optional reason) - this only writes, and answers whether it landed
  async function toggleLock(email, reason = null) {
    if (!email || !testDb) return false
    const isLocked = !!lockedByEmail[email.toLowerCase()]
    const { error } = await testDb.from('agent_license').upsert(
      { email, active: isLocked, reason: isLocked ? null : reason, updated_at: new Date().toISOString() },
      { onConflict: 'email' }
    )
    if (error) { console.error('agent_license upsert failed', error); window.alert(t('common_action_failed')); return false }
    const { data } = await testDb.from('agent_license').select('*')
    setLicRows(data || [])
    return true
  }
  // "Latest" = the newest build ever sighted, read from the global `app_versions`
  // ledger — the SAME source the server's GET /api/test/latest-version uses.
  //
  // ⚠️ DO NOT derive this from `agent_app` (the previous bug, 2026-06-30): each
  // agent_app row's `first_seen` is the agent's ONBOARDING time (set once on row
  // insert, never updated when their app_version changes — the heartbeat upsert
  // only writes user_email/app_version/last_seen). So "max agent_app.first_seen"
  // returned the version of the most-recently-onboarded agent, not the newest
  // build. That made the genuinely-newest build show ⚠ outdated while an older
  // build showed as latest. `app_versions` records each version's global first
  // sighting once (rollback-immune), so its newest first_seen is the real latest.
  const [latestVersion, setLatestVersion] = useState(null)
  useEffect(() => {
    if (!testDb) return
    testDb.from('app_versions')
      .select('app_version,first_seen')
      .eq('status', 'active')   // a revoked (faulty) build must never be "latest" — admin rolls back in Extension CFG
      .order('first_seen', { ascending: false })
      .limit(1)
      .then(({ data }) => setLatestVersion(data?.[0]?.app_version || null))
  }, [])

  const eventsByUser = useMemo(() => groupByUser(events), [events])

  // Build rows: agents employed on the viewed date + event logins no roster agent owns.
  const rows = useMemo(() => {
    // Only agents who were employed on this date (resigned agents reappear on
    // past days they were still working). Anyone not employed is excluded,
    // including their leftover event rows.
    const visibleAgents = agents.filter(a => employedOn(a, date))
    // ⚠️ OFFICE SCOPE (audit 2026-10-21): every login of the WHOLE roster is "known". With an office selected the
    // events are still read agency-wide, and the other offices' agents used to come back as extra rows - Delisi
    // showed "17 active of 14". An event whose login belongs to ANY roster agent is that agent's, shown only with
    // that agent; only a login nobody owns becomes an "unmatched" row.
    const knownKeys = new Set()
    allAgents.forEach(a => agentEmailKeys(a).forEach(k => knownKeys.add(k)))

    const agentRows = visibleAgents.map(a => {
      const keys = agentEmailKeys(a)
      // Merge + dedupe events across all of this agent's keys (login + contact email)
      const seen = new Set()
      const evs = keys.flatMap(k => eventsByUser[k] || []).filter(e => {
        const id = e.id ?? `${e.session_id}-${e.created_at}`
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
      return {
        id: a.id,
        email: keys[0] || null,   // primary = login email, used for version lookup
        label: a.name || a.email || '—',
        events: evs,
        status: statusOf(a, date, attByAgent, scheduleHistory, overrideMap),
        checkIn: checkInOf(a.id, date, attByAgent),
        shifts: scheduledSlotsOnDate(a, date, scheduleHistory, overrideMap) || [],
      }
    })
    // Unmatched event-only logins (e.g. test accounts) → their own section, outside every total.
    const extraRows = Object.entries(eventsByUser)
      .filter(([k]) => !knownKeys.has(k))
      .map(([k, evs]) => ({ id: 'x:' + k, email: k, label: k, events: evs, status: 'unmatched' }))

    return [...agentRows, ...extraRows].sort((a, b) => {
      const g = GROUP_ORDER[a.status] - GROUP_ORDER[b.status]
      if (g !== 0) return g
      // Sort by ACTIVITY within each section: total events (did they work at all),
      // then published. Idle agents (0 events) sink to the bottom instead of being
      // interleaved with active ones by name. Name is only the final tiebreak.
      if (b.events.length !== a.events.length) return b.events.length - a.events.length
      const pa = a.events.filter(e => e.published).length
      const pb = b.events.filter(e => e.published).length
      if (pb !== pa) return pb - pa
      return a.label.localeCompare(b.label)
    })
  }, [agents, allAgents, eventsByUser, date, attByAgent, scheduleHistory, overrideMap])
  // every agent's PC automation switch (owner 2026-10-20, managers / admins only - the server answers 403 to others,
  // and then no switch is drawn), refreshed every 2 minutes
  const [workerByLogin, setWorkerByLogin] = useState({})
  const workerLogins = useMemo(() => rows.map(r => String(r.email || '').toLowerCase()).filter(e => e.endsWith('@sentavus.com')).map(e => e.split('@')[0]).sort().join(','), [rows])
  useEffect(() => {
    if (!workerLogins) return
    let live = true
    const load = () => getWorkerSwitches(workerLogins.split(',')).then(d => { if (live) setWorkerByLogin((d && d.states) || {}) }).catch(() => {})
    load()
    const t2 = setInterval(load, 2 * 60 * 1000)
    return () => { live = false; clearInterval(t2) }
  }, [workerLogins])
  // answers whether the write landed - the hours picker stays open (with the choice) when it did not
  const changePaidOverride = async (login, mode, hours) => {
    try { const s = await setPaidOverride(login, mode, hours)
      if (s && s.ok) { setWorkerByLogin(m => ({ ...m, [login]: { ...m[login], paid: { ...(m[login]?.paid || {}), override: s.override || null, held: !!s.held } } })); return true }
      window.alert(t('common_action_failed')); return false }
    catch (e) { window.alert(e.message); return false }
  }
  const changeWorkerPart = async (login, part, off) => {
    try { const s = await setWorkerSwitchPart(login, part, off); if (s && s.mode) setWorkerByLogin(m => ({ ...m, [login]: { ...m[login], ...s } })) } catch {}
  }

  // ⚠️ Headline = the agents ON SCREEN (the selected office), by the one outcome classifier. Unmatched logins are
  // listed apart and never counted. "Published" is a posting SESSION with a captured link - not a verified live
  // advert (a Facebook session can be several group posts) - and automatic sessions are split from manual ones.
  const totals = useMemo(() => {
    const c = { published: 0, failed: 0, filled: 0, pending: 0, auto: 0 }
    const perPlat = {}   // platform -> { pub, fail }: the summary tiles' per-portal line (owner 2026-11-03)
    let active = 0, autoOnly = 0
    for (const r of rows) {
      if (r.status === 'unmatched') continue
      let pub = 0, manual = 0
      for (const e of r.events) {
        const o = outcomeOf(e)
        c[o]++
        if (e.platform && (o === 'published' || o === 'failed')) {
          const pp = (perPlat[e.platform] ||= { pub: 0, fail: 0 })
          if (o === 'published') pp.pub++; else pp.fail++
        }
        if (o === 'published') { pub++; if (isAutoPost(e, autoIds)) c.auto++; else manual++ }
      }
      if (pub > 0) { active++; if (manual === 0) autoOnly++ }
    }
    return { ...c, perPlat, active, autoOnly }
  }, [rows, autoIds])
  // Live clock — only ticks while viewing today; drives the pulsing "now" marker.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    if (!isToday) return
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [isToday])
  const nowMinute = isToday ? tbilisiMinuteOfDay(now.toISOString()) : null

  const unmatchedCount = rows.filter(r => r.status === 'unmatched').length
  const loginOf = r => (r.email ? String(r.email).toLowerCase().split('@')[0] : null)

  // ── "My day" (owner 2026-11-03) ─────────────────────────────────────────────
  const myRow = agentView && myAgentId ? rows.find(r => r.id === myAgentId) || null : null
  // The agent's OWN switches and PC: the team read (worker-switches) answers 403 to an agent, so their card asks the
  // agent-scoped my-worker + ext-sessions instead - the same two reads the header's worker panel makes, every 2 minutes.
  const [myWorker, setMyWorker] = useState(null)
  useEffect(() => {
    if (!agentView || !myAgentId) return
    let live = true
    const load = () => Promise.all([getMyWorker().catch(() => null), getExtSessions().catch(() => null)]).then(([w, x]) => {
      if (!live) return
      const pcs = x?.sessions || null
      const best = pcs ? pcs.slice().sort((a, b) => (b.online - a.online) || (Date.parse(b.last_seen || 0) - Date.parse(a.last_seen || 0)))[0] : null
      if (!w?.mode && !pcs) { setMyWorker(null); return }
      setMyWorker({ ...(w || {}), pc: w?.pc || (best ? { online: !!best.online, seen_at: best.last_seen || null } : null) })
    })
    load()
    const id = setInterval(() => { if (document.visibilityState === 'visible') load() }, 2 * 60 * 1000)
    return () => { live = false; clearInterval(id) }
  }, [agentView, myAgentId])


  // Meeting minutes widen their own bins — see buildScale's header.
  // the width every rail has (one column template), reported by the rails themselves - the scale fills it
  const [railW, setRailW] = useState(0)
  const scale = useMemo(
    () => buildScale(events, [...[...meetingsByAgent.values()].flat(), ...[...coopsByAgent.values()].flat()].map(m => m.minute), railW),
    [events, meetingsByAgent, coopsByAgent, railW])
  // one shared horizontal position for the axis and every agent's rail (see useScrollToNow)
  const timelineSync = useRef({ els: new Set(), left: null, setW: null }).current
  timelineSync.setW = setRailW
  // the "Now" control: puts the current minute back in view on every rail (they follow whichever one is moved).
  // ⚠️ The axis is its own component (AxisScroll) since 2026-11-03: it can mount AFTER this tab does (an agent unfolds
  // the team), and a hook held here would have registered a ref that was still empty - the axis never joined the shared
  // position and read 00-11 above rails showing 10-15.
  const scrollToNow = () => {
    const el = timelineSync.els.values().next().value
    if (!el || nowMinute == null) return
    el.scrollLeft = Math.max(0, scale.xForMinute(nowMinute) - el.clientWidth * 0.6)
  }

  // Step the viewed date by ±1 day (never past today).
  function shiftDay(delta) {
    const d = new Date(date + 'T12:00:00')
    d.setDate(d.getDate() + delta)
    const next = toDateStr(d)
    if (next > tbilisiToday()) return
    setDate(next)
  }

  // group rows into sections
  const sections = GROUPS.map(g => ({ ...g, rows: rows.filter(r => r.status === g.key) }))
                         .filter(s => s.rows.length > 0)

  const fetchedClock = day.fetchedAt && loaded ? fmtClock(day.fetchedAt.toISOString()) : null
  const behind = loaded && (error || (isToday && day.fetchedAt && now - day.fetchedAt > STALE_AFTER_MS))
  const kpi = v => (loaded && !(error && !day.fetchedAt) ? v : null)   // '—' until THIS day's events are in

  // what the status zone holds on THIS page - decided once, so every row has the same slots (an empty slot keeps its line)
  const hasWorker = Object.keys(workerByLogin).length > 0
  const rowSlots = hasWorker ? ['pc', 'updater', 'version', 'access'] : ['version', 'access']
  // the actions zone exists on every row of a page where any row can have a ⋯ - so the counts column never shifts
  const hasActions = canLock || hasWorker

  // PAGE STRUCTURE (owner 2026-11-03, one structure instead of pieces added over time). Fixed bands, top to bottom:
  //   1 header (day, freshness, refresh)  2 "My day" (agents; the "Needs attention" list was taken out
  //   again the same day - owner 2026-11-03 "dont need this list here")
  //   3 summary band (KPI tiles + admin balance gauges)  4 team: sticky axis, sections, rows  5 legend  6 monthly chart
  // Every band is a CARD with px-4; the bands are space-y-4 apart.
  return (
    <TipContext.Provider value={showTip}>
    <div className="space-y-4">
      <TipPopover tip={tip} onClose={closeTip} />

      {/* 1 · HEADER — the TITLE is gone (owner 2026-08-03): the tab above already says აქტივობა. What is left is WHICH DAY
          and since 2026-10-21 HOW FRESH: when the numbers were read, and Refresh. */}
      <div className={`${CARD} px-4 py-2 flex items-center gap-2`}>
        <button type="button" onClick={() => setReloadTick(n => n + 1)} title={t('listings_refresh')} aria-label={t('listings_refresh')}
          className={`${BTN} shrink-0 text-foreground hover:bg-accent max-sm:w-8 max-sm:px-0`}>
          <Icon name="refresh-cw" size={12} /><span className="max-sm:hidden">{t('listings_refresh')}</span></button>
        <div className="min-w-0 shrink-0 text-xs text-muted-foreground" aria-live="polite">
          {!loaded
            ? <span>{t('listings_loading')}</span>
            : behind
              ? <span className={`inline-flex items-center gap-1 ${TONE.warn.text}`} title={fetchedClock ? tLog('listings_stale', { t: fetchedClock }) : t('listings_unavailable')}>
                  <Icon name="alert-triangle" size={12} /><span className="max-sm:hidden">{fetchedClock ? tLog('listings_stale', { t: fetchedClock }) : t('listings_unavailable')}</span><span className="sm:hidden tabular-nums">{fetchedClock || '—'}</span></span>
              : fetchedClock && <span title={tLog('listings_updated_at', { t: fetchedClock })}><span className="max-sm:hidden">{tLog('listings_updated_at', { t: fetchedClock })}</span><span className="sm:hidden tabular-nums">{fetchedClock}</span></span>}
        </div>
        <div className="ml-auto flex items-center gap-1 shrink-0">
          <button type="button" onClick={() => shiftDay(-1)} title={t('listings_prev_day')} aria-label={t('listings_prev_day')}
            className={`${BTN} w-8 px-0 hover:bg-accent`}>‹</button>
          <DateInput value={date} max={tbilisiToday()} onChange={v => { if (v) setDate(v) }}
            className="flex items-center gap-0.5 rounded-md border border-input px-1.5 h-8 text-xs bg-background" />
          {/* on a phone a disabled "next day" (viewing today) gives its room to the update time */}
          <button type="button" onClick={() => shiftDay(1)} disabled={isToday} title={t('listings_next_day')} aria-label={t('listings_next_day')}
            className={`${BTN} w-8 px-0 hover:bg-accent disabled:opacity-40 disabled:cursor-not-allowed ${isToday ? 'max-sm:hidden' : ''}`}>›</button>
          {!isToday && (
            <button type="button" onClick={() => setDate(tbilisiToday())} title={t('listings_today')} aria-label={t('listings_today')}
              className={`${BTN} hover:bg-accent max-sm:w-8 max-sm:px-0`}><Icon name="calendar" size={12} /><span className="max-sm:hidden">{t('listings_today')}</span></button>
          )}
        </div>
      </div>

      {/* An empty roster is the difference between "Darina" and "darinam@..." on every row, between shift bands and none,
          and between a meeting marker landing and vanishing (markers key on agents.id). Say it, never fail silently. */}
      {rosterFromServer && (
        <div className={`${CARD} px-4 py-2 text-xs text-muted-foreground flex items-center gap-1.5`}>
          <Icon name="info" size={12} />{t('listings_roster_fallback')}
        </div>
      )}
      {agents.length === 0 && events.length > 0 && (
        <div className={`${CARD} px-4 py-2 text-xs flex items-center gap-1.5 ${TONE.warn.text}`}>
          <Icon name="alert-triangle" size={12} />{t('listings_no_roster')}
        </div>
      )}

      {/* 2 · MY DAY (agents) or NEEDS ATTENTION (admin / manager) */}
      {agentView && myAgentId && loaded && (myRow
        ? <MyDayCard row={myRow} scale={scale} nowMinute={nowMinute} sync={timelineSync}
            meetings={meetingsByAgent.get(myRow.id) || EMPTY} nudgeTiers={nudgeTiers} autoIds={autoIds}
            balances={myRow.email ? balancesByEmail[myRow.email.toLowerCase()] : null}
            worker={myWorker} versionInfo={appByEmail[myRow.email]} latestVersion={latestVersion}
            coops={coopsByAgent.get(myRow.id) || EMPTY} />
        : <div className={`${CARD} px-4 py-2 text-xs text-muted-foreground flex items-center gap-1.5`}><Icon name="info" size={12} />{t('listings_myday_missing')}</div>)}

      {/* 3 · SUMMARY BAND. The meetings tile (owner 2026-08-03) counts the day's APPOINTMENTS beside the day's postings;
          its headline is what was on the calendar, the subline how it went. Ghosts (moved away) are never in the
          headline. The admin's portal balance gauges (owner 2026-11-02) are tiles of the same band. */}
      <section className={`${CARD} px-4 py-3 space-y-2`} aria-label={t(agentView ? 'listings_team_today' : 'listings_summary')}>
        {/* named, so an agent does not read it as a second copy of "My day" right above it */}
        <h3 className="text-xs font-semibold text-muted-foreground">{t(agentView ? 'listings_team_today' : 'listings_summary')}</h3>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
          <StatTile label={t('listings_kpi_published')} value={kpi(totals.published)} tone={totals.published > 0 ? 'ok' : null}
            sub={kpi(totals.published) != null && autoIds ? tLog('listings_kpi_pub_split', { a: totals.auto, m: totals.published - totals.auto }) : null}>
            {kpi(totals.published) != null && <PlatformSplit perPlat={totals.perPlat} field="pub" />}
          </StatTile>
          <StatTile label={t('listings_kpi_failed')} value={kpi(totals.failed)} tone={totals.failed > 0 ? 'bad' : null}
            sub={kpi(totals.failed) != null ? tLog('listings_kpi_fail_sub', { p: totals.pending, f: totals.filled }) : null}>
            {kpi(totals.failed) != null && <PlatformSplit perPlat={totals.perPlat} field="fail" />}
          </StatTile>
          <StatTile label={t('listings_kpi_meetings')} value={meetingsDown ? null : meetingTotals.held}
            sub={meetingsDown ? t('listings_unavailable') : meetingTotals.sub} subTone={meetingsDown ? 'warn' : null} />
          {/* cooperations opened today, by the agents on screen (owner 2026-11-03) - the diamonds on the rails, counted */}
          <StatTile label={t('listings_kpi_coops')} value={rows.filter(r => r.status !== 'unmatched').reduce((n, r) => n + (coopsByAgent.get(r.id)?.length || 0), 0)} />
          <StatTile label={t('listings_kpi_active')} value={kpi(totals.active)}
            sub={tLog('listings_kpi_of', { n: visibleIds.size })
              + (kpi(totals.autoOnly) ? ' · ' + tLog('listings_kpi_auto_only', { n: totals.autoOnly }) : '')} />
        </div>
        {role === 'admin' && Object.keys(balancesByEmail).length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {[['myhome', 'MyHome'], ['ss', 'SS.ge']].map(([p, label]) => (
              <BalanceGauge key={p} label={label} rows={Object.values(balancesByEmail).map(b => b[p]).filter(Boolean)} wallet={walletByPortal[p] || null} />
            ))}
          </div>
        )}
        {nudgesDown && (
          <div className="text-xs text-muted-foreground flex items-center gap-1.5"><Icon name="info" size={12} />{t('listings_nudges_unavailable')}</div>
        )}
      </section>

      {/* 4 · TEAM — folded for an agent (their own day is the card above) */}
      {agentView && loaded && rows.length > 0 && (
        <button type="button" onClick={() => setTeamOpen(o => !o)} aria-expanded={teamOpen}
          className={`${CARD} w-full flex items-center justify-between gap-2 px-4 h-11 text-sm font-medium hover:bg-accent`}>
          <span>{teamOpen ? t('listings_team_hide') : tLog('listings_team_show', { n: rows.filter(r => r.status !== 'unmatched').length })}</span>
          <span className={`inline-flex text-muted-foreground transition-transform ${teamOpen ? 'rotate-180' : ''}`} aria-hidden="true"><Icon name="chevron-down" size={14} /></span>
        </button>
      )}
      {teamOpen && <>
      {/* The shared hour axis - the ruler every row is read against, so it stays on top and STICKY (audit 2026-10-21).
          Laid out by the same <Zones> as every row, so every hour sits above the same minute on every rail. The note
          that the scale is NOT linear sits on it - equal gaps are not equal idle time. */}
      <div className={`sticky z-[15] ${CARD} overflow-hidden shadow-sm`} style={{ top: headerH }}>
        <Zones className="px-4 py-1 items-center"
          identity={<TipChip text={t('listings_legend_hint')} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground max-w-full">
            <Icon name="info" size={12} /><span className="truncate max-sm:hidden">{t('listings_axis_short')}</span></TipChip>}
          rail={<AxisScroll scale={scale} nowMinute={nowMinute} sync={timelineSync} />}
          counts={nowMinute != null ? (
            <button type="button" onClick={scrollToNow} className={`${BTN} ${TONE.bad.text} font-semibold hover:bg-accent`}>{t('listings_now')}</button>
          ) : null}
        />
      </div>

      {!loaded ? (
        <div className={`${CARD} px-4 py-16 text-center text-muted-foreground text-sm`}>{t('listings_loading')}</div>
      ) : error && !day.fetchedAt ? (
        <div className={`${CARD} px-4 py-8 text-center text-sm ${TONE.bad.text}`}>{error}</div>
      ) : rows.length === 0 ? (
        <div className={`${CARD} px-4 py-16 text-center text-muted-foreground text-sm`}>{t('listings_no_agents')}</div>
      ) : (
        sections.map(section => (
          <section key={section.key} className={`${CARD} overflow-hidden`}>
            <Zones className="px-4 py-2 border-b border-border/60"
              identity={<div className={`flex items-center gap-1.5 text-xs font-semibold ${section.color}`}>
                <Icon name={section.icon} size={12} /><span>{t(section.labelKey)}</span>
                <span className="text-muted-foreground font-normal tabular-nums">({section.rows.length})</span></div>} />
            {section.rows.map(r => (
              <AgentRow key={r.id} rowId={r.id} agentLabel={r.label} events={r.events}
                        status={r.status} scale={scale} shifts={r.shifts || EMPTY} nowMinute={nowMinute}
                        checkIn={r.checkIn || null} slots={rowSlots} hasActions={hasActions}
                        versionInfo={appByEmail[r.email]} latestVersion={latestVersion}
                        meetings={meetingsByAgent.get(r.id) || EMPTY}
                        coops={coopsByAgent.get(r.id) || EMPTY}
                        nudgeTiers={nudgeTiers}
                        balances={r.email ? balancesByEmail[r.email.toLowerCase()] : null}
                        autoIds={autoIds}
                        locked={!!(r.email && lockedByEmail[r.email.toLowerCase()])}
                        onToggleLock={canLock && r.email ? (reason) => toggleLock(r.email, reason) : undefined}
                        worker={r.email ? workerByLogin[loginOf(r)] || null : null}
                        onSetWorkerPart={r.email ? (part, off) => changeWorkerPart(loginOf(r), part, off) : undefined}
                        onPaidOverride={role === 'admin' && r.email ? (mode, hours) => changePaidOverride(loginOf(r), mode, hours) : null}
                        sync={timelineSync} />
            ))}
          </section>
        ))
      )}
      {unmatchedCount > 0 && officeFilterActive && (
        <div className={`${CARD} px-4 py-2 text-xs text-muted-foreground`}>{t('listings_unmatched_note')}</div>
      )}
      </>}

      {/* 5 · LEGEND (owner 2026-08-03: at the foot - a key is read once, when a glyph is unfamiliar). It lists EXACTLY what
          the page draws, in the page's own colours (TONE / PIN_COLOR / AUTO_FILL / SHIFT_FILL / NUDGE_FILL / MEETING_*):
          the pins an agent POSTED, the strips and bands on a rail, and the glyphs they ATTENDED, one line each. */}
      <section className={`${CARD} overflow-hidden text-xs text-muted-foreground`} aria-label={t('listings_legend_title')}>
        <LegendLine label={t('listings_legend_listings')}>
          {Object.entries(PIN_COLOR).map(([p, c]) => (
            <LegendItem key={p} swatch={<span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: c }} />}>{PLATFORM_LABEL[p]}</LegendItem>
          ))}
          <LegendItem swatch={<span className="w-2.5 h-2.5 rounded-full border-2 border-muted-foreground" />}>{t('listings_legend_pending')}</LegendItem>
          <LegendItem swatch={<Icon name="x" size={12} />}>{t('listings_legend_failed')}</LegendItem>
        </LegendLine>
        <LegendLine label={t('listings_legend_rail')}>
          <LegendItem swatch={<span className="w-4 h-2.5 rounded-sm" style={{ backgroundColor: SHIFT_FILL.office.tint, border: `1px solid ${SHIFT_FILL.office.edge}` }} />}>{t('listings_legend_shift')}</LegendItem>
          <LegendItem swatch={<span className="w-4 h-2.5 rounded-sm" style={{ backgroundColor: SHIFT_FILL.home.tint, border: `1px solid ${SHIFT_FILL.home.edge}` }} />}>{t('listings_legend_shift_home')}</LegendItem>
          <LegendItem swatch={<span className="w-4 h-1 rounded-sm" style={{ backgroundColor: AUTO_FILL }} />}>{t('listings_auto_on')}</LegendItem>
          <LegendItem swatch={<span className="w-4 h-1 rounded-sm" style={{ backgroundColor: TONE.muted.fill }} />}>{t('listings_auto_off')}</LegendItem>
          <LegendItem swatch={<span className="w-4 h-1 rounded-sm" style={{ backgroundColor: TONE.ok.fill }} />}>{t('listings_pc_online')}</LegendItem>
          <LegendItem swatch={<span className="w-1 h-2.5 rounded-t-sm" style={{ backgroundColor: TONE.bad.fill }} />}>{t('listings_legend_pc_gap')}</LegendItem>
          <LegendItem swatch={<span className="w-4 h-1 rounded-sm" style={{ backgroundColor: TONE.bad.fill }} />}>{t('listings_pc_offline')}</LegendItem>
          <LegendItem swatch={<span className="w-px h-3" style={{ backgroundColor: TONE.bad.fill }} />}>{t('listings_now')}</LegendItem>
          <LegendItem swatch={<span className="inline-block w-[8px] h-[8px] rotate-45 rounded-[1px] border" style={{ backgroundColor: COOP_FILL, borderColor: COOP_EDGE }} />}>{t('listings_legend_coop')}</LegendItem>
          <span className="italic">{t('listings_legend_hint')}</span>
        </LegendLine>
        {/* Meetings, EACH GLYPH NAMED (owner 2026-08-03): a row of unlabelled icons with one word after it is not a key */}
        <LegendLine label={t('listings_legend_meeting')}>
          {Object.entries(MEETING_ICON).map(([st, name]) => (
            <LegendItem key={st} swatch={<Icon name={name} size={14} className={MEETING_TONE[st]} />}
              style={{ opacity: MEETING_OPACITY[st] != null ? Math.max(MEETING_OPACITY[st], 0.55) : 1 }}>
              {st === 'moved' ? t('listings_legend_moved') : st === 'revisited' ? t('listings_meeting_revisited') : t(MEETING_STATUS_KEYS[st] || '')}
            </LegendItem>
          ))}
          {Object.entries(NUDGE_FILL).map(([tier, c]) => (
            <LegendItem key={tier} swatch={<span className="w-2 h-2 rounded-full" style={{ backgroundColor: c }} />}>{t(`listings_legend_nudge_${tier}`)}</LegendItem>
          ))}
        </LegendLine>
      </section>

      {/* 6 · MONTHLY CHART — daily published counts with a month switcher. Agency-wide: with an office selected it says so. */}
      <LazyMount>
        <MonthlyAdsChart selectedDate={date}
          scopeNote={officeFilterActive ? t('listings_month_scope_all') : null}
          onPickDay={(d) => { if (d <= tbilisiToday()) setDate(d) }} />
      </LazyMount>
    </div>
    </TipContext.Provider>
  )
}

function LegendLine({ label, children }) {
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-2 border-b border-border/60 last:border-0">
      <span className="font-medium text-foreground w-24 shrink-0">{label}</span>
      {children}
    </div>
  )
}
function LegendItem({ swatch, children, style }) {
  return <span className="inline-flex items-center gap-1.5" style={style}><span className="inline-flex items-center justify-center">{swatch}</span>{children}</span>
}
